import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { ReportsService } from '../../services/reports.service';
import { CrmTarget } from './crm-notes-panel/crm-notes-panel.component';

declare const google: any;

export interface SalesRouteStop {
	debtorno: string;
	branchcode: string;
	name: string;
	brname: string;
	address: string;
	city: string;
	state: string;
	zip: string;
	lat: number;
	lng: number;
	salesman: string;
	territory_id: string;
	phoneno?: string;
	email?: string;
	salesmanname?: string;
	territory_name?: string;
	priority?: number;            // 1=high, 2=med, 3=low
	needs_geocode?: number;
	visit_count?: number;
	days_since_visit?: number | null;
	last_visit_date?: string | null;
	last_order_date?: string | null;
	stop_id?: number;
	stop_type?: 'customer' | 'prospect';
	prospect_key?: string;
	stop_status?: 'pending' | 'done' | 'skipped';
	est_potential?: number | null;   // leads: estimated annual $ opportunity
	license_type?: string;           // leads: license category
	ops_at_addr?: number;            // leads: businesses at the address
	est_twin?: string;               // leads: comparable existing customer
	assigned?: boolean;              // leads: assigned to this rep
	owner?: string;                  // leads: license holder / owner (for convert prefill)
}

export interface ProspectPin {
	prospect_key: string;
	business_name: string;
	owner: string;
	license_type: string;
	address: string;
	city: string;
	zip5: string;
	lat: number;
	lng: number;
	ops_at_addr: number;
	est_potential: number | null;
	est_twin: string;
	rep_name: string;
	assigned: boolean;
}

interface RouteTemplate {
	template_id: number;
	zone: string;
	territory: string;
	route_code: string;
	route_name: string;
	territory_id: string;
	estimated_stops: number;
}

/** One chip in the rolling day strip — a real calendar date, which resolves
 *  to a (Monday-week, weekday-index) pair for storage. */
interface PlannerDay {
	weekStart: string;   // ISO Monday of the week this date belongs to
	dow: number;         // 0=Mon … 4=Fri within that week
	label: string;       // Mon/Tue/…
	full: string;        // Monday/…
	dateShort: string;   // 7/9
	isToday: boolean;
	newWeek: boolean;    // first chip of a fresh week (draws a divider)
}

type PoolFilter = 'all' | 'overdue' | 'never' | 'needs_geo' | 'priority';
type EndMode = 'home' | 'last' | 'custom';
interface DayPoint {
	home_base_lat: number | null; home_base_lng: number | null; home_base_label: string;
	end_mode: EndMode; end_lat: number | null; end_lng: number | null; end_label: string;
}

@Component({
	selector: 'app-sales-route-builder',
	templateUrl: './sales-route-builder.component.html',
	styleUrls: ['./sales-route-builder.component.scss']
})
export class SalesRouteBuilderComponent implements OnInit, AfterViewInit, OnDestroy {
	config: any;
	metaLoaded = false;
	loadingMeta = false;
	loadingAccounts = false;
	optimizing = false;
	saving = false;
	loadingWeek = false;
	loadingTemplates = false;
	applyingTemplate = false;
	geocoding = false;
	checkingIn = false;
	planningAi = false;
	// Status is a floating toast that dismisses itself — every existing
	// `this.status = …` call site gets the behavior for free via the setter.
	private _status: string | null = null;
	private statusTimer: any = null;
	get status(): string | null { return this._status; }
	set status(v: string | null) {
		this._status = v;
		if (this.statusTimer) { clearTimeout(this.statusTimer); this.statusTimer = null; }
		if (v) {
			this.statusTimer = setTimeout(() => { this._status = null; this.cdr?.markForCheck(); }, 7000);
		}
	}

	// One-level undo for bulk / destructive edits (lasso, clear day, AI plan…).
	private undoSnap: { weeks: string; week: string; dow: number; label: string } | null = null;
	get canUndo(): boolean { return this.undoSnap != null; }

	salespeople: any[] = [];
	territories: { territory_id: string; territory_name: string }[] = [];

	salesmanCtrl = new UntypedFormControl('');
	territoryCtrl = new UntypedFormControl('');

	allAccounts: SalesRouteStop[] = [];

	readonly days = [
		{ dow: 0, label: 'Mon', full: 'Monday' },
		{ dow: 1, label: 'Tue', full: 'Tuesday' },
		{ dow: 2, label: 'Wed', full: 'Wednesday' },
		{ dow: 3, label: 'Thu', full: 'Thursday' },
		{ dow: 4, label: 'Fri', full: 'Friday' },
	];
	// Every loaded week's plan, keyed by ISO Monday. The rolling day strip can
	// span two of these, so we hold them all and edit whichever day is active.
	private weeks: Record<string, Record<number, SalesRouteStop[]>> = {};
	private dirtyWeeks = new Set<string>();
	activeWeekStart = '';
	activeDow = 0;
	windowStart = '';           // ymd of the first chip in the rolling strip
	plannerDays: PlannerDay[] = [];

	// Active week's day map. Getter/setter keep all the existing
	// `routesByDay[dow]` reads/writes and `routesByDay = fresh` assignments working.
	get routesByDay(): Record<number, SalesRouteStop[]> {
		if (!this.activeWeekStart) { return { 0: [], 1: [], 2: [], 3: [], 4: [] }; }
		if (!this.weeks[this.activeWeekStart]) { this.weeks[this.activeWeekStart] = { 0: [], 1: [], 2: [], 3: [], 4: [] }; }
		return this.weeks[this.activeWeekStart];
	}
	set routesByDay(v: Record<number, SalesRouteStop[]>) {
		if (this.activeWeekStart) { this.weeks[this.activeWeekStart] = v; }
	}

	// Kept as an alias of the active week for draft keys + runner handoff.
	get weekStart(): string { return this.activeWeekStart; }
	set weekStart(v: string) { this.activeWeekStart = v; }
	savedWeeks: { week_start: string; stop_count: number; updated_at: string }[] = [];
	lastWeekHadPlan = false;

	templates: RouteTemplate[] = [];
	templateZones: { zone: string; n: number }[] = [];
	templateZoneCtrl = new UntypedFormControl('');
	showTemplates = false;
	showDayTools = false;

	// Pool search + filter
	poolSearch = '';
	poolFilter: PoolFilter = 'all';

	// Side panel
	sideTab: 'route' | 'accounts' | 'prospects' | 'nearby' = 'route';
	prospectSearch = '';
	leadFilter: 'all' | 'assigned' = 'all';
	leadRadius: number | null = null;   // miles; null = any distance
	readonly leadRadiusOptions = [10, 25, 50];
	// "Near my stops": customers + leads close to the active day's route.
	nearbyRadiusPlan = 5;
	readonly nearbyRadiusOptions = [3, 5, 10];
	nearbyIncludeLeads = true;
	private nearbyCacheKey = '';
	private nearbyCacheRows: Array<{ kind: 'customer' | 'lead'; dist: number; key: string; acct?: SalesRouteStop; lead?: ProspectPin }> = [];
	prospectTotal = 0;
	unsaved = false;
	private fpCacheKey = '';
	private fpCacheRows: ProspectPin[] = [];

	// Rep mode: a logged-in salesman gets the stripped-down, one-person view —
	// no rep/territory pickers, no manager tools, auto-save doing the thinking.
	isRep = false;
	repName = '';
	private autoSaveTimer: any = null;

	// Route settings
	showSettings = false;
	homeBaseLabel = '';
	homeBaseLat: number | null = null;
	homeBaseLng: number | null = null;
	// Tap-the-map to drop a custom start / end point.
	settingPoint: 'none' | 'start' | 'end' = 'none';
	endMode: EndMode = 'home';
	endLat: number | null = null;
	endLng: number | null = null;
	endLabel = '';
	endAddr = '';
	// Per-day start/end overrides — homeBaseLat/endMode/etc above are the WEEK-level
	// default; a day with an entry here uses that instead. Absent = "same as the week".
	// Keyed by weekStart, then day_of_week. See migrations/sales_route_day_points.sql —
	// added because a single shared point per week produces phantom mileage whenever a
	// rep's week spans multiple regions on different days.
	private dayPointOverrides: Record<string, Record<number, DayPoint>> = {};
	private weekDefaults: Record<string, DayPoint> = {};

	// Check-in
	checkinStop: SalesRouteStop | null = null;
	checkinOutcome = 'met';
	checkinNotes = '';
	readonly checkinOutcomes = [
		{ v: 'met', l: 'Met with customer' },
		{ v: 'no_contact', l: 'No contact' },
		{ v: 'left_sample', l: 'Left sample' },
		{ v: 'called', l: 'Called' },
		{ v: 'other', l: 'Other' },
	];

	// AI plan
	showAi = false;
	aiDays: Record<string, boolean> = { Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true };
	aiMin = 5; aiTarget = 8; aiMax = 12; aiNotes = '';
	aiPace: 'light' | 'normal' | 'full' = 'normal';
	aiAdvanced = false;
	aiResult: any = null;

	// Save-as-template
	showTemplateSave = false;
	tmplZone = '';
	tmplName = '';

	// Territory manager
	showTerritoryMgr = false;
	tmgrId: number | null = null;
	tmgrLoading = false;
	tmgrBranches: any[] = [];
	tmgrSearch = '';
	tmgrTotal = 0;
	tmgrSearched = false;
	private tmgrSearchTimer: any = null;

	// Prospect layer (AA beautician prospecting)
	showProspects = false;
	prospectsAvailable = true;
	loadingProspects = false;
	prospects: ProspectPin[] = [];
	private prospectMarkers: any[] = [];
	private prospectInfoWin: any = null;

	// CRM drawer
	crmTarget: CrmTarget | null = null;

	// Draft
	draftRestored = false;
	private draftTimer: any = null;

	lastOptimizeMiles: number | null = null;
	mapError: string | null = null;

	// Lasso / map draw
	@ViewChild('srbMapHost') mapHost!: ElementRef<HTMLDivElement>;
	@ViewChild('srbLasso') lassoCanvas!: ElementRef<SVGSVGElement>;
	drawMode: 'none' | 'lasso' | 'lasso_remove' = 'none';
	drawHint = '';
	private lassoPts: Array<{ x: number; y: number }> = [];
	private lassoDrawing = false;
	private lassoPointerId: number | null = null;
	private lassoSavedGest: any = null;
	private lassoDown = (e: PointerEvent) => this.onLassoDown(e);
	private lassoMove = (e: PointerEvent) => this.onLassoMove(e);
	private lassoUp = (e: PointerEvent) => this.onLassoUp(e);
	private blockTouch = (e: Event) => e.preventDefault();
	private poolMarkers: any[] = [];
	private clusterListenerAttached = false;
	private readonly clusterCellPx = 46;

	private map: any;
	private mapMarkers: any[] = [];
	private routeLine: any;
	private metaSub?: Subscription;
	private userSub?: Subscription;

	constructor(
		public globalSearchService: GlobalSearchService,
		private reportsService: ReportsService,
		private locationSvc: Location,
		private router: Router,
		private cdr: ChangeDetectorRef
	) {
		this.globalSearchService.mesagesallowed.next(false);
		this.globalSearchService.aiDataAvailable$.next(null);
		this.globalSearchService.configsubscription.subscribe((r) => {
			this.config = r;
			this.globalSearchService.getColor();
		});
	}

	ngOnInit(): void {
		this.globalSearchService.getColor();
		this.initTodayDefaults();
		const u = this.globalSearchService.user.getValue()?.user?.defaultlocation;
		this.homeBaseLat = this.num(u?.lat);
		this.homeBaseLng = this.num(u?.lng);
		this.homeBaseLabel = u?.locationname || 'Home base';
		this.loadMeta();
		this.loadTemplates();
		this.userSub = this.globalSearchService.user.subscribe(() => this.loadMeta());
	}

	ngAfterViewInit(): void { this.tryInitMap(); }

	ngOnDestroy(): void {
		this.metaSub?.unsubscribe();
		this.userSub?.unsubscribe();
		if (this.draftTimer) { clearTimeout(this.draftTimer); }
		if (this.statusTimer) { clearTimeout(this.statusTimer); this.statusTimer = null; }
		// Leaving with edits pending: flush them so a rep never loses work.
		if (this.autoSaveTimer) { clearTimeout(this.autoSaveTimer); this.autoSaveTimer = null; }
		if (this.unsaved) { this.saveWeek(true); }
		this.exitLasso();
		this.clearMapOverlays();
	}

	back(): void { this.locationSvc.back(); }
	authUserid(): string {
		const u = this.globalSearchService.user.getValue()?.user;
		return u?.userid != null ? String(u.userid) : '';
	}
	private num(v: any): number | null { const n = parseFloat(String(v ?? '')); return Number.isFinite(n) ? n : null; }

	// ── week helpers ────────────────────────────────────────────────
	/**
	 * Open on TODAY, not on a Monday that's already behind us: active day =
	 * today's weekday, and on Sat/Sun roll straight into next week's Monday.
	 * (weekStart stays the ISO Monday — that's the server's week key.)
	 */
	/** Rolling window starts TODAY (weekends jump to Monday), and the active
	 *  day is today. The strip then runs forward across the week boundary — no
	 *  dead past days. */
	private initTodayDefaults(): void {
		const now = new Date();
		const dow = now.getDay(); // 0=Sun … 6=Sat
		const anchor = new Date(now);
		if (dow === 0) { anchor.setDate(anchor.getDate() + 1); }        // Sun → Mon
		else if (dow === 6) { anchor.setDate(anchor.getDate() + 2); }   // Sat → Mon
		this.windowStart = this.fmt(anchor);
		this.buildWindow();
		const first = this.plannerDays[0];
		this.activeWeekStart = first.weekStart;
		this.activeDow = first.dow;
	}

	/** Build the 5 working days shown in the strip, starting at windowStart. */
	private buildWindow(): void {
		const todayYmd = this.fmt(new Date());
		const out: PlannerDay[] = [];
		let cur = this.parseYmd(this.windowStart);
		let prevWeek = '';
		while (out.length < 5) {
			const wd = cur.getDay();
			if (wd !== 0 && wd !== 6) {
				const ws = this.mondayOf(cur);
				const idx = wd - 1;
				out.push({
					weekStart: ws, dow: idx,
					label: this.days[idx].label, full: this.days[idx].full,
					dateShort: `${cur.getMonth() + 1}/${cur.getDate()}`,
					isToday: this.fmt(cur) === todayYmd,
					newWeek: ws !== prevWeek && prevWeek !== '',
				});
				prevWeek = ws;
			}
			cur = new Date(cur.getTime() + 86400000);
		}
		this.plannerDays = out;
	}

	private parseYmd(s: string): Date {
		const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
		return new Date(y, m - 1, d);
	}
	private mondayOf(d: Date): string {
		const cp = new Date(d);
		const dow = cp.getDay();
		const back = dow === 0 ? 6 : dow - 1;
		cp.setHours(0, 0, 0, 0);
		cp.setDate(cp.getDate() - back);
		return this.fmt(cp);
	}
	private fmt(d: Date): string {
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}
	/** Label for the whole rolling window, e.g. "Jul 9 – Jul 15". */
	weekLabel(): string {
		if (!this.plannerDays.length) { return ''; }
		const f = (ymd: string) => this.parseYmd(ymd).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
		const first = this.plannerDays[0];
		const last = this.plannerDays[this.plannerDays.length - 1];
		const firstYmd = this.dayYmd(first);
		const lastYmd = this.dayYmd(last);
		return `${f(firstYmd)} – ${f(lastYmd)}`;
	}
	private dayYmd(d: PlannerDay): string {
		const dt = this.parseYmd(d.weekStart);
		dt.setDate(dt.getDate() + d.dow);
		return this.fmt(dt);
	}
	prevWeek(): void { this.shiftWindow(-7); }
	nextWeek(): void { this.shiftWindow(7); }
	thisWeek(): void { this.initTodayDefaults(); this.loadWeek(); }
	private shiftWindow(delta: number): void {
		const dt = this.parseYmd(this.windowStart);
		dt.setDate(dt.getDate() + delta);
		// Never let the window start on a weekend.
		const wd = dt.getDay();
		if (wd === 0) { dt.setDate(dt.getDate() + 1); }
		else if (wd === 6) { dt.setDate(dt.getDate() + 2); }
		this.windowStart = this.fmt(dt);
		this.buildWindow();
		const first = this.plannerDays[0];
		this.activeWeekStart = first.weekStart;
		this.activeDow = first.dow;
		this.lastFitDow = -1;
		this.loadWeek();
	}
	private prevWeekStart(): string {
		const dt = this.parseYmd(this.activeWeekStart);
		dt.setDate(dt.getDate() - 7);
		return this.fmt(dt);
	}

	/** Weeks the strip currently spans (1 or 2). */
	private windowWeeks(): string[] {
		return Array.from(new Set(this.plannerDays.map((d) => d.weekStart)));
	}

	selectDay(d: PlannerDay): void {
		this.activeWeekStart = d.weekStart;
		this.activeDow = d.dow;
		if (!this.weeks[d.weekStart]) { this.loadWeekData(d.weekStart, false); }
		this.lastOptimizeMiles = null;
		this.lastFitDow = -1;
		this.refreshMap();
	}
	isActiveDay(d: PlannerDay): boolean { return d.weekStart === this.activeWeekStart && d.dow === this.activeDow; }

	setSideTab(tab: 'route' | 'accounts' | 'prospects' | 'nearby'): void {
		this.sideTab = tab;
		if (tab === 'prospects') {
			// The list and the map layer are the same data — light both up.
			if (!this.prospects.length && !this.loadingProspects) { this.showProspects = true; this.loadProspects(); }
			else if (!this.showProspects) { this.showProspects = true; this.repaintProspects(); }
		}
		if (tab === 'nearby') {
			// Nearby wants leads on the map too so they can be surfaced.
			if (this.nearbyIncludeLeads && !this.prospects.length && !this.loadingProspects) { this.showProspects = true; this.loadProspects(); }
			this.nearbyCacheKey = '';
		}
		// The map mirrors the active tab, so repaint when it changes.
		this.refreshMap();
	}
	setNearbyRadius(r: number): void { this.nearbyRadiusPlan = r; this.nearbyCacheKey = ''; this.refreshMap(); }
	toggleNearbyLeads(): void {
		this.nearbyIncludeLeads = !this.nearbyIncludeLeads;
		if (this.nearbyIncludeLeads && !this.prospects.length && !this.loadingProspects) { this.showProspects = true; this.loadProspects(); }
		this.nearbyCacheKey = '';
		this.refreshMap();
	}

	/** Coordinates of the active day's routed stops (the anchors we measure from). */
	private activeStopPoints(): Array<{ lat: number; lng: number }> {
		return this.activeStops.filter((s) => this.hasCoord(s)).map((s) => ({ lat: s.lat, lng: s.lng }));
	}
	private distToStops(lat: number, lng: number, anchors: Array<{ lat: number; lng: number }>): number {
		let min = Infinity;
		for (const a of anchors) { const d = this.haversineMiles(a.lat, a.lng, lat, lng); if (d < min) { min = d; } }
		return min;
	}
	/** Customers + leads within the chosen radius of the day's stops, nearest first. */
	nearbyToStops(): Array<{ kind: 'customer' | 'lead'; dist: number; key: string; acct?: SalesRouteStop; lead?: ProspectPin }> {
		const anchors = this.activeStopPoints();
		const key = `${this.activeWeekStart}|${this.activeDow}|${anchors.length}|${this.nearbyRadiusPlan}|${this.nearbyIncludeLeads}|${this.allAccounts.length}|${this.prospects.length}|${this.totalStops()}`;
		if (key === this.nearbyCacheKey) { return this.nearbyCacheRows; }
		const out: Array<{ kind: 'customer' | 'lead'; dist: number; key: string; acct?: SalesRouteStop; lead?: ProspectPin }> = [];
		if (anchors.length) {
			const R = this.nearbyRadiusPlan;
			const onRoute = new Set(this.allPlacedStops().map((s) => this.stopKey(s)));
			for (const a of this.allAccounts) {
				if (a.needs_geocode || !this.hasCoord(a) || onRoute.has(this.stopKey(a))) { continue; }
				const d = this.distToStops(a.lat, a.lng, anchors);
				if (d <= R) { out.push({ kind: 'customer', dist: d, key: this.stopKey(a), acct: a }); }
			}
			if (this.nearbyIncludeLeads) {
				for (const p of this.prospects) {
					if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) { continue; }
					const k = `p|${p.prospect_key}`;
					if (onRoute.has(k)) { continue; }
					const d = this.distToStops(p.lat, p.lng, anchors);
					if (d <= R) { out.push({ kind: 'lead', dist: d, key: k, lead: p }); }
				}
			}
			out.sort((x, y) => x.dist - y.dist);
		}
		this.nearbyCacheKey = key;
		this.nearbyCacheRows = out;
		return out;
	}
	nearbyCount(): number { return this.nearbyToStops().length; }
	/** Keys of the nearby set, for focusing the map on the Nearby tab. */
	private nearbyKeySet(): Set<string> { return new Set(this.nearbyToStops().map((r) => r.key)); }
	addNearbyRow(r: { kind: 'customer' | 'lead'; acct?: SalesRouteStop; lead?: ProspectPin }): void {
		if (r.kind === 'customer' && r.acct) { this.addToRoute(r.acct); }
		else if (r.kind === 'lead' && r.lead) { this.addProspectToRoute(r.lead); }
		this.nearbyCacheKey = '';
	}
	// Template-safe accessors (AOT can't narrow the optional acct/lead by kind).
	nearbyName(r: { kind: 'customer' | 'lead'; acct?: SalesRouteStop; lead?: ProspectPin }): string {
		return r.kind === 'lead' ? (r.lead ? (r.lead.business_name || r.lead.owner) : '') : (r.acct ? this.accountLabel(r.acct) : '');
	}
	nearbyVisit(r: { acct?: SalesRouteStop }): string { return r.acct ? this.visitBadge(r.acct) : ''; }
	nearbyPotential(r: { lead?: ProspectPin }): number | null { return r.lead?.est_potential ?? null; }
	openNearby(r: { kind: 'customer' | 'lead'; acct?: SalesRouteStop; lead?: ProspectPin }): void {
		if (r.kind === 'lead' && r.lead) { this.openLead(r.lead); }
		else if (r.acct) { this.openCustomer360(r.acct); }
	}
	/** Add every nearby customer + lead to the active day in one tap. */
	addAllNearby(): void {
		const rows = this.nearbyToStops();
		if (!rows.length) { return; }
		this.snapshotForUndo('add all nearby');
		const list = this.routesByDay[this.activeDow];
		const have = new Set(this.allPlacedStops().map((s) => this.stopKey(s)));
		let added = 0, leads = 0;
		for (const r of rows) {
			if (have.has(r.key)) { continue; }
			if (r.kind === 'customer' && r.acct) { list.push({ ...r.acct }); added++; }
			else if (r.kind === 'lead' && r.lead) { list.push(this.stopFromProspect(r.lead)); leads++; }
			have.add(r.key);
		}
		this.routesByDay[this.activeDow] = [...list];
		if (added || leads) { this.status = `Added ${this.addedSummary(added, leads)} near your stops to ${this.days[this.activeDow].label}.`; }
		this.nearbyCacheKey = '';
		this.afterMutate();
	}

	/** Leads matching search + filter, minus ones already routed. Uncapped (memoized — templates call this a lot). */
	matchingProspects(): ProspectPin[] {
		const key = `${this.prospectSearch}|${this.leadFilter}|${this.leadRadius}|${this.prospects.length}|${this.totalStops()}`;
		if (key === this.fpCacheKey) { return this.fpCacheRows; }
		const q = this.prospectSearch.trim().toLowerCase();
		let rows = this.prospects;
		if (this.leadFilter === 'assigned') { rows = rows.filter((p) => p.assigned); }
		if (q) {
			rows = rows.filter((p) => `${p.business_name} ${p.owner} ${p.city} ${p.zip5}`.toLowerCase().includes(q));
		}
		if (this.leadRadius != null) {
			rows = rows.filter((p) => this.leadDistance(p) <= this.leadRadius!);
		}
		const onRoute = new Set<string>();
		for (const s of this.allPlacedStops()) { if (s.stop_type === 'prospect') { onRoute.add(s.prospect_key || ''); } }
		let out = rows.filter((p) => !onRoute.has(p.prospect_key));
		// When filtering by distance, nearest-first is what a rep wants.
		if (this.leadRadius != null) { out = out.slice().sort((a, b) => this.leadDistance(a) - this.leadDistance(b)); }
		this.fpCacheKey = key;
		this.fpCacheRows = out;
		return this.fpCacheRows;
	}
	setLeadRadius(r: number | null): void { this.leadRadius = r; this.fpCacheKey = ''; this.repaintProspects(); }
	onLeadSearch(): void { this.fpCacheKey = ''; this.repaintProspects(); }
	onPoolSearch(): void { this.repaintPool(); }

	/** Reloaded lead stops lose est_potential (not persisted) — restore it from
	 *  the loaded leads list by key so per-day opportunity survives a reload. */
	private backfillProspectStops(): void {
		if (!this.prospects.length) { return; }
		const byKey = new Map(this.prospects.map((p) => [p.prospect_key, p]));
		for (const ws of Object.keys(this.weeks)) {
			const wk = this.weeks[ws];
			for (const dow of [0, 1, 2, 3, 4]) {
				for (const s of (wk[dow] || [])) {
					if (s.stop_type === 'prospect' && s.est_potential == null && s.prospect_key) {
						const p = byKey.get(s.prospect_key);
						if (p) { s.est_potential = p.est_potential; s.license_type = p.license_type; s.est_twin = p.est_twin; s.ops_at_addr = p.ops_at_addr; s.assigned = p.assigned; }
					}
				}
			}
		}
	}
	/** Every stop placed across all loaded weeks (for dedupe / already-routed checks). */
	allPlacedStops(): SalesRouteStop[] {
		const out: SalesRouteStop[] = [];
		for (const ws of Object.keys(this.weeks)) {
			const wk = this.weeks[ws];
			for (const dow of [0, 1, 2, 3, 4]) { for (const s of (wk[dow] || [])) { out.push(s); } }
		}
		return out;
	}
	/** The list renders at most 400 rows — search narrows the rest. */
	filteredProspects(): ProspectPin[] { return this.matchingProspects().slice(0, 400); }
	prospectsCapped(): boolean { return this.matchingProspects().length > 400; }
	assignedCount(): number { return this.prospects.reduce((n, p) => n + (p.assigned ? 1 : 0), 0); }
	setLeadFilter(f: 'all' | 'assigned'): void { this.leadFilter = f; this.fpCacheKey = ''; this.repaintProspects(); }

	licenseLabel(t: string): string {
		if (t.indexOf('BARB') >= 0) { return 'Barber shop'; }
		if (t.indexOf('RENTER') >= 0) { return 'Booth renter'; }
		if (t.indexOf('BUSINESS') >= 0) { return 'Salon'; }
		return t;
	}
	get activeStops(): SalesRouteStop[] { return this.routesByDay[this.activeDow] || []; }
	/** Public accessor for a (week, day)'s stops — templates can't read the private weeks map. */
	stopsForDay(weekStart: string, dow: number): SalesRouteStop[] {
		const wk = this.weeks[weekStart];
		return wk ? (wk[dow] || []) : [];
	}
	/** Stops on a specific (week, day) — the strip crosses weeks so both are needed. */
	dayCount(weekStart: string, dow: number): number {
		const wk = this.weeks[weekStart];
		return wk ? (wk[dow] || []).length : 0;
	}
	/** Stops across every day shown in the rolling strip. */
	totalStops(): number { return this.plannerDays.reduce((n, d) => n + this.dayCount(d.weekStart, d.dow), 0); }
	activeDayLabel(): string { return this.days[this.activeDow] ? this.days[this.activeDow].label : ''; }

	// ── distance / mileage (client-side haversine, matches the server) ──────
	private haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
		const R = 3958.8, toRad = (d: number) => (d * Math.PI) / 180;
		const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
		const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
		return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	}
	private hasCoord(s: { lat?: number; lng?: number }): boolean {
		return Number.isFinite(s.lat) && Number.isFinite(s.lng) && !(s.lat === 0 && s.lng === 0);
	}
	/** The week-level default point, used when a day has no override of its own. */
	private weekDefaultPoint(ws: string): DayPoint {
		return this.weekDefaults[ws] || {
			home_base_lat: this.homeBaseLat, home_base_lng: this.homeBaseLng, home_base_label: this.homeBaseLabel,
			end_mode: this.endMode, end_lat: this.endLat, end_lng: this.endLng, end_label: this.endLabel,
		};
	}
	/** The start/end point actually in effect for one day — its own override, else the week's default. */
	private effectivePoint(ws: string, dow: number): DayPoint {
		return this.dayPointOverrides[ws]?.[dow] || this.weekDefaultPoint(ws);
	}
	/** Whether the ACTIVE day has a start/end point different from the rest of its week. */
	get hasDayOverride(): boolean { return !!this.dayPointOverrides[this.weekStart]?.[this.activeDow]; }
	get effHomeBaseLat(): number | null { return this.effectivePoint(this.weekStart, this.activeDow).home_base_lat; }
	get effHomeBaseLng(): number | null { return this.effectivePoint(this.weekStart, this.activeDow).home_base_lng; }
	get effHomeBaseLabel(): string { return this.effectivePoint(this.weekStart, this.activeDow).home_base_label; }
	get effEndMode(): EndMode { return this.effectivePoint(this.weekStart, this.activeDow).end_mode; }
	get effEndLat(): number | null { return this.effectivePoint(this.weekStart, this.activeDow).end_lat; }
	get effEndLng(): number | null { return this.effectivePoint(this.weekStart, this.activeDow).end_lng; }
	get effEndLabel(): string { return this.effectivePoint(this.weekStart, this.activeDow).end_label; }
	/** Two-way binding for the End-point <select> — writes go to this day's override. */
	get effEndModeModel(): EndMode { return this.effEndMode; }
	set effEndModeModel(v: EndMode) { this.setDayOverride({ end_mode: v }); this.afterMutate(); }
	/** Snapshot of what's currently in effect for the active day, used as the merge
	 *  base when the rep changes only the start OR only the end. */
	private setDayOverride(patch: Partial<DayPoint>): void {
		const base = this.effectivePoint(this.weekStart, this.activeDow);
		if (!this.dayPointOverrides[this.weekStart]) { this.dayPointOverrides[this.weekStart] = {}; }
		this.dayPointOverrides[this.weekStart][this.activeDow] = { ...base, ...patch };
	}
	/** Revert the active day back to sharing the week's default start/end point. */
	clearDayOverride(): void {
		const wk = this.dayPointOverrides[this.weekStart];
		if (wk) { delete wk[this.activeDow]; }
		this.afterMutate();
	}
	/** Estimated drive miles for a day's ordered stops (home → stops → end).
	 *  Pass weekStart/dow so a day's OWN start/end point is used — omit only
	 *  for legacy callers that already resolved against the active day. */
	dayMiles(stops: SalesRouteStop[], weekStart?: string, dow?: number): number {
		const pts = stops.filter((s) => this.hasCoord(s));
		if (!pts.length) { return 0; }
		const pt = (weekStart != null && dow != null) ? this.effectivePoint(weekStart, dow) : this.weekDefaultPoint(this.weekStart);
		let total = 0;
		const px = pt.home_base_lat, py = pt.home_base_lng;
		const haveHome = px != null && py != null;
		if (haveHome) { total += this.haversineMiles(px!, py!, pts[0].lat, pts[0].lng); }
		for (let i = 1; i < pts.length; i++) {
			total += this.haversineMiles(pts[i - 1].lat, pts[i - 1].lng, pts[i].lat, pts[i].lng);
		}
		// Return-to-base leg only when the day ends at home.
		if (haveHome && pt.end_mode === 'home') {
			total += this.haversineMiles(pts[pts.length - 1].lat, pts[pts.length - 1].lng, px!, py!);
		} else if (pt.end_mode === 'custom' && pt.end_lat != null && pt.end_lng != null) {
			total += this.haversineMiles(pts[pts.length - 1].lat, pts[pts.length - 1].lng, pt.end_lat, pt.end_lng);
		}
		return total;
	}
	activeMiles(): number { return this.dayMiles(this.activeStops, this.weekStart, this.activeDow); }
	/** Drive miles into stop i — from the start point for the first stop,
	 *  from the previous stop otherwise. Shown on each route row. */
	legMiles(i: number): number | null {
		const s = this.activeStops[i];
		if (!s || !this.hasCoord(s)) { return null; }
		if (i === 0) {
			if (this.effHomeBaseLat == null || this.effHomeBaseLng == null) { return null; }
			return this.haversineMiles(this.effHomeBaseLat, this.effHomeBaseLng, s.lat, s.lng);
		}
		const p = this.activeStops[i - 1];
		if (!p || !this.hasCoord(p)) { return null; }
		return this.haversineMiles(p.lat, p.lng, s.lat, s.lng);
	}
	weekMiles(): number {
		return this.plannerDays.reduce((n, d) => {
			const wk = this.weeks[d.weekStart];
			return n + (wk ? this.dayMiles(wk[d.dow] || [], d.weekStart, d.dow) : 0);
		}, 0);
	}

	/** Where "distance to a lead" is measured from: home base, else the customer centroid, else NY. */
	private leadOrigin(): { lat: number; lng: number } {
		if (this.homeBaseLat != null && this.homeBaseLng != null) { return { lat: this.homeBaseLat, lng: this.homeBaseLng }; }
		const geo = this.allAccounts.filter((a) => this.hasCoord(a));
		if (geo.length) {
			return { lat: geo.reduce((s, a) => s + a.lat, 0) / geo.length, lng: geo.reduce((s, a) => s + a.lng, 0) / geo.length };
		}
		return this.defaultCenter;
	}
	leadDistance(p: ProspectPin): number {
		const o = this.leadOrigin();
		return this.haversineMiles(o.lat, o.lng, p.lat, p.lng);
	}

	// ── meta + accounts ─────────────────────────────────────────────
	private loadMeta(): void {
		this.loadingMeta = true;
		this.metaSub?.unsubscribe();
		this.metaSub = this.reportsService.getSalesRouteMeta().subscribe({
			next: (res: any) => {
				this.loadingMeta = false;
				if (res?.success !== false) { this.salespeople = res.salespeople || []; this.territories = res.territories || []; }
				this.metaLoaded = true;
				this.applySalesmanDefault();
				this.cdr.markForCheck();
			},
			error: () => { this.loadingMeta = false; this.metaLoaded = true; this.cdr.markForCheck(); }
		});
	}
	private applySalesmanDefault(): void {
		const u = this.globalSearchService.user.getValue()?.user;
		if (!u?.issalesman) { return; }
		this.isRep = true;
		const code = String(u.salesman ?? '').trim();
		if (!code || this.salesmanCtrl.value) { return; }
		// A rep's own code may not be in the manager dropdown list (that list is
		// group_id=1 AND current=1) — load THEIR data regardless of list
		// membership, so they never see a "pick a salesperson" dead-end.
		const match = this.salespeople.find((sp) => String(sp?.salesmancode ?? '').trim() === code);
		this.repName = String(match?.salesmanname ?? u.salesmanname ?? u.name ?? '').trim() || code;
		this.salesmanCtrl.setValue(code, { emitEvent: false });
		this.loadWeek();
		this.loadAccounts();
	}
	onSalesmanChange(): void {
		this.loadWeek();
		this.loadAccounts();
		if (this.showProspects) { this.loadProspects(); }
	}

	loadAccounts(): void {
		const uid = this.authUserid();
		if (!uid) { return; }
		const payload: Record<string, unknown> = { userid: uid };
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		if (sm) { payload.salesman = sm; }
		const terr = String(this.territoryCtrl.value ?? '').trim();
		if (terr) { payload.territory = terr; }
		this.loadingAccounts = true;
		this.reportsService.postSalesRouteAccounts(payload).subscribe({
			next: (res: any) => {
				this.loadingAccounts = false;
				const rows = (res?.success ? (res.accounts || []) : []) as any[];
				this.allAccounts = rows.map((r) => this.normStop(r));
				this.cdr.markForCheck();
				setTimeout(() => this.refreshMap(), 0);
			},
			error: () => { this.loadingAccounts = false; this.cdr.markForCheck(); }
		});
	}

	// ── pool view ───────────────────────────────────────────────────
	filteredAccounts(): SalesRouteStop[] {
		const q = this.poolSearch.trim().toLowerCase();
		let rows = this.allAccounts;
		if (q) {
			rows = rows.filter((a) => `${a.name} ${a.brname} ${a.city} ${a.debtorno} ${a.zip}`.toLowerCase().includes(q));
		}
		switch (this.poolFilter) {
			case 'overdue': rows = rows.filter((a) => { const d = this.lastContactDays(a); return d != null && d >= 30; }); break;
			case 'never': rows = rows.filter((a) => this.lastContactDays(a) == null); break;
			case 'needs_geo': rows = rows.filter((a) => a.needs_geocode); break;
			case 'priority': rows = rows.filter((a) => (a.priority ?? 2) === 1); break;
		}
		return rows;
	}
	setPoolFilter(f: PoolFilter): void { this.poolFilter = f; this.repaintPool(); }

	private daysSince(ymd: string | null | undefined): number | null {
		if (!ymd) { return null; }
		const d = new Date(String(ymd).slice(0, 10) + 'T00:00:00');
		if (isNaN(d.getTime())) { return null; }
		const today = new Date(); today.setHours(0, 0, 0, 0);
		return Math.max(0, Math.floor((today.getTime() - d.getTime()) / 86400000));
	}
	/** Days since the rep last touched this account — a logged visit OR an order. */
	lastContactDays(a: SalesRouteStop): number | null {
		const v = this.daysSince(a.last_visit_date);
		const o = this.daysSince(a.last_order_date);
		if (v == null && o == null) { return null; }
		if (v == null) { return o; }
		if (o == null) { return v; }
		return Math.min(v, o);
	}
	/** "Visited 3d ago" / "Ordered 12d ago" / "Never" — reflects real activity. */
	visitBadge(a: SalesRouteStop): string {
		const v = this.daysSince(a.last_visit_date);
		const o = this.daysSince(a.last_order_date);
		const d = this.lastContactDays(a);
		if (d == null) { return 'Never'; }
		const kind = (v != null && (o == null || v <= o)) ? 'Visited' : 'Ordered';
		return d === 0 ? `${kind} today` : `${kind} ${d}d ago`;
	}
	needsGeoCount(): number { return this.allAccounts.filter((a) => a.needs_geocode).length; }

	/** Stable identity for either stop kind — customers by branch, prospects by MD5 key. */
	stopKey(s: { debtorno?: string; branchcode?: string; stop_type?: string; prospect_key?: string }): string {
		return s.stop_type === 'prospect' ? `p|${s.prospect_key}` : `${s.debtorno}|${s.branchcode}`;
	}

	// ── per-day editing ─────────────────────────────────────────────
	addToRoute(a: SalesRouteStop): void {
		if (a.needs_geocode) { this.status = `${a.name} needs GPS — geocode it first.`; return; }
		// Don't silently double-book the same customer on two days (matches leads).
		const already = this.onDayLabelForKey(this.stopKey(a));
		if (already) { this.status = `${a.name || a.brname} is already on ${already}.`; this.cdr.markForCheck(); return; }
		const list = this.routesByDay[this.activeDow];
		this.routesByDay[this.activeDow] = [...list, { ...a }];
		this.afterMutate();
	}
	removeFromRoute(idx: number): void { this.routesByDay[this.activeDow] = this.activeStops.filter((_, i) => i !== idx); this.afterMutate(); }
	dropReorder(event: CdkDragDrop<SalesRouteStop[]>): void {
		if (event.previousIndex === event.currentIndex) { return; }
		const next = [...this.activeStops];
		moveItemInArray(next, event.previousIndex, event.currentIndex);
		this.routesByDay[this.activeDow] = next; this.afterMutate();
	}
	cyclePriority(s: SalesRouteStop): void { s.priority = ((s.priority ?? 2) % 3) + 1; this.unsaved = true; if (this.activeWeekStart) { this.dirtyWeeks.add(this.activeWeekStart); } this.persistDraftSoon(); this.autoSaveSoon(); }
	priorityLabel(p?: number): string { return p === 1 ? 'High' : p === 3 ? 'Low' : 'Med'; }
	/** Which day (label) in the rolling window this account is already on, or ''. */
	onDayLabel(a: SalesRouteStop): string {
		for (const d of this.plannerDays) {
			const wk = this.weeks[d.weekStart];
			if (wk && (wk[d.dow] || []).some((s) => s.debtorno === a.debtorno && s.branchcode === a.branchcode)) {
				return d.label;
			}
		}
		return '';
	}
	/** True when the account is on the currently-selected day. */
	isOnActiveDay(a: SalesRouteStop): boolean {
		return this.activeStops.some((s) => this.stopKey(s) === this.stopKey(a));
	}
	/** Which day-label in the window already holds this stop key (customer or lead), or ''. */
	onDayLabelForKey(key: string): string {
		for (const d of this.plannerDays) {
			const wk = this.weeks[d.weekStart];
			if (wk && (wk[d.dow] || []).some((s) => this.stopKey(s) === key)) { return d.label; }
		}
		return '';
	}
	clearDay(): void {
		if (!this.activeStops.length) { return; }
		this.snapshotForUndo(`empty ${this.days[this.activeDow].label}`);
		this.routesByDay[this.activeDow] = [];
		this.status = `${this.days[this.activeDow].label} emptied.`;
		this.afterMutate();
	}
	copyDay(fromDow: number): void {
		if (fromDow === this.activeDow) { return; }
		this.snapshotForUndo(`copy ${this.days[fromDow].label}`);
		const src = this.routesByDay[fromDow] || [];
		const seen = new Set(this.activeStops.map((s) => this.stopKey(s)));
		const merged = [...this.activeStops];
		for (const s of src) { const k = this.stopKey(s); if (!seen.has(k)) { merged.push({ ...s }); seen.add(k); } }
		this.routesByDay[this.activeDow] = merged; this.afterMutate();
	}
	copyLastWeek(): void {
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		if (!sm) { return; }
		this.reportsService.getSalesRouteWeek(sm, this.prevWeekStart()).subscribe({
			next: (res: any) => {
				if (!res?.success || !Array.isArray(res.stops) || !res.stops.length) { this.status = 'No plan found for last week.'; this.cdr.markForCheck(); return; }
				const fresh: Record<number, SalesRouteStop[]> = { 0: [], 1: [], 2: [], 3: [], 4: [] };
				for (const o of res.stops) { const dow = Number(o.day_of_week); if (fresh[dow]) { fresh[dow].push(this.normStop(o)); } }
				this.routesByDay = fresh; this.status = 'Copied last week.'; this.afterMutate();
			},
			error: () => { this.status = 'Could not load last week.'; this.cdr.markForCheck(); }
		});
	}

	/** Capture the plan before a bulk/destructive edit so the toast can offer Undo. */
	private snapshotForUndo(label: string): void {
		this.undoSnap = { weeks: JSON.stringify(this.weeks), week: this.activeWeekStart, dow: this.activeDow, label };
	}
	undoLast(): void {
		if (!this.undoSnap) { return; }
		this.weeks = JSON.parse(this.undoSnap.weeks);
		this.activeWeekStart = this.undoSnap.week;
		this.activeDow = this.undoSnap.dow;
		const label = this.undoSnap.label;
		this.undoSnap = null;
		this.afterMutate();
		this.status = `Undid “${label}”.`;
	}

	private afterMutate(): void {
		this.lastOptimizeMiles = null;
		this.unsaved = true;
		if (this.activeWeekStart) { this.dirtyWeeks.add(this.activeWeekStart); }
		this.fpCacheKey = '';
		this.refreshMap();
		this.persistDraftSoon();
		this.autoSaveSoon();
		this.cdr.markForCheck();
	}

	/**
	 * Auto-save: reps forget the Save button, so every edit schedules a
	 * debounced silent save to the server. The manual "Save week" button
	 * stays for reassurance and for the moments before the debounce fires.
	 */
	private autoSaveSoon(): void {
		if (!String(this.salesmanCtrl.value ?? '').trim()) { return; }
		if (this.autoSaveTimer) { clearTimeout(this.autoSaveTimer); }
		this.autoSaveTimer = setTimeout(() => this.saveWeek(true), 1500);
	}

	private normStop(o: any): SalesRouteStop {
		return {
			debtorno: String(o.debtorno ?? ''), branchcode: String(o.branchcode ?? ''),
			name: String(o.name ?? ''), brname: String(o.brname ?? ''),
			address: String(o.address ?? ''), city: String(o.city ?? ''),
			state: String(o.state ?? ''), zip: String(o.zip ?? ''),
			lat: Number(o.lat), lng: Number(o.lng),
			salesman: String(o.salesman ?? ''), territory_id: String(o.territory_id ?? ''),
			phoneno: o.phoneno, email: o.email, salesmanname: o.salesmanname, territory_name: o.territory_name,
			priority: o.priority != null ? Number(o.priority) : 2,
			needs_geocode: o.needs_geocode != null ? Number(o.needs_geocode) : (Number.isFinite(Number(o.lat)) && Number(o.lat) !== 0 ? 0 : 1),
			visit_count: o.visit_count != null ? Number(o.visit_count) : 0,
			days_since_visit: o.days_since_visit != null ? Number(o.days_since_visit) : null,
			last_visit_date: o.last_visit_date ?? null,
			last_order_date: o.last_order_date ?? null,
			stop_id: o.stop_id != null ? Number(o.stop_id) : undefined,
			stop_type: o.stop_type === 'prospect' ? 'prospect' : 'customer',
			prospect_key: String(o.prospect_key ?? ''),
			stop_status: (o.stop_status === 'done' || o.stop_status === 'skipped') ? o.stop_status : 'pending',
		};
	}

	// ── optimize active day (priority + recency weighting server-side) ──
	optimizeRoute(): void {
		const stopsIn = this.activeStops.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
		if (stopsIn.length < 1) { return; }
		const uid = this.authUserid();
		if (!uid) { return; }
		const beforeMiles = this.activeMiles();
		this.optimizing = true;
		this.reportsService.postSalesRouteOptimize({
			userid: uid,
			stops: stopsIn.map((s) => ({ id: this.stopKey(s), ...s })),
			start: this.startPoint(),
			end_mode: this.effEndMode,
			end: this.effEndMode === 'custom' && this.effEndLat != null ? { lat: this.effEndLat, lng: this.effEndLng } : undefined,
		}).subscribe({
			next: (res: any) => {
				this.optimizing = false;
				if (res?.success && Array.isArray(res.optimized)) {
					this.snapshotForUndo('best driving order');
					this.routesByDay[this.activeDow] = res.optimized.map((o: any) => this.normStop(o));
					this.lastOptimizeMiles = res.total_distance_miles != null ? Number(res.total_distance_miles) : null;
					this.afterMutate();
					// Tell the rep what it actually bought them.
					const saved = beforeMiles - this.activeMiles();
					this.status = saved > 0.5
						? `Best driving order — about ${Math.round(saved)} mi shorter.`
						: 'This is already the best driving order.';
				}
				this.cdr.markForCheck();
			},
			error: () => { this.optimizing = false; this.cdr.markForCheck(); }
		});
	}
	private startPoint(): { lat: number; lng: number } {
		if (this.effHomeBaseLat != null && this.effHomeBaseLng != null) { return { lat: this.effHomeBaseLat, lng: this.effHomeBaseLng }; }
		// Centroid of only the geocoded stops — one NaN coord would poison the average.
		const list = this.activeStops.filter((s) => this.hasCoord(s));
		if (list.length) {
			let slat = 0, slng = 0; for (const s of list) { slat += s.lat; slng += s.lng; }
			return { lat: slat / list.length, lng: slng / list.length };
		}
		return this.defaultCenter;
	}

	// ── geocoding ───────────────────────────────────────────────────
	// The server-side geocoder needs a self-hosted Nominatim that is not
	// configured on every host, so the primary path is the Google Maps JS
	// geocoder (already loaded for the map) with the result persisted via
	// sales_route_save_coords; the server endpoint stays as the fallback.
	private googleGeocoder: any = null;
	private googleGeocode(address: string): Promise<{ lat: number; lng: number } | null> {
		if (typeof google === 'undefined' || !google.maps?.Geocoder) { return Promise.resolve(null); }
		if (!this.googleGeocoder) { this.googleGeocoder = new google.maps.Geocoder(); }
		return new Promise((resolve) => {
			this.googleGeocoder.geocode({ address }, (results: any[], gstatus: string) => {
				if (gstatus === 'OK' && results?.length) {
					const loc = results[0].geometry.location;
					resolve({ lat: loc.lat(), lng: loc.lng() });
				} else { resolve(null); }
			});
		});
	}
	private stopFullAddress(a: SalesRouteStop): string {
		return [a.address, a.city, a.state || 'NY', a.zip].filter((x) => String(x || '').trim()).join(', ');
	}
	private inNY(lat: number, lng: number): boolean {
		return lat >= 40.40 && lat <= 45.10 && lng >= -79.85 && lng <= -71.75;
	}
	/** Google-first geocode of one account; persists to custbranch on success. */
	private async geocodeViaGoogle(a: SalesRouteStop): Promise<boolean> {
		const addr = this.stopFullAddress(a);
		if (!addr) { return false; }
		const geo = await this.googleGeocode(addr);
		if (!geo || !this.inNY(geo.lat, geo.lng)) { return false; }
		const persisted = await new Promise<boolean>((resolve) => {
			this.reportsService.postSalesRouteSaveCoords({
				debtorno: a.debtorno, branchcode: a.branchcode, lat: geo.lat, lng: geo.lng, address: addr,
			}).subscribe({ next: (res: any) => resolve(!!res?.success), error: () => resolve(false) });
		});
		if (persisted) { a.lat = geo.lat; a.lng = geo.lng; a.needs_geocode = 0; }
		return persisted;
	}
	geocodeOne(a: SalesRouteStop): void {
		this.geocoding = true; this.status = null;
		this.geocodeViaGoogle(a).then((ok) => {
			if (ok) {
				this.geocoding = false;
				this.refreshMap();
				this.cdr.markForCheck();
				return;
			}
			// Fall back to the server-side geocoder.
			this.reportsService.postSalesRouteGeocode({ debtorno: a.debtorno, branchcode: a.branchcode }).subscribe({
				next: (res: any) => {
					this.geocoding = false;
					if (res?.success) { a.lat = Number(res.lat); a.lng = Number(res.lng); a.needs_geocode = 0; this.refreshMap(); }
					else { this.status = res?.message || 'Could not geocode that address.'; }
					this.cdr.markForCheck();
				},
				error: () => { this.geocoding = false; this.status = 'Geocoding failed.'; this.cdr.markForCheck(); }
			});
		});
	}
	async bulkGeocodeMissing(): Promise<void> {
		const missing = this.allAccounts.filter((a) => a.needs_geocode).slice(0, 100);
		if (!missing.length) { this.status = 'Nothing to geocode.'; return; }
		if (typeof google === 'undefined' || !google.maps?.Geocoder) {
			// No Google JS — use the server batch endpoint (cap 25 per call).
			this.bulkGeocodeViaServer(missing.slice(0, 25));
			return;
		}
		this.geocoding = true;
		let ok = 0, failed = 0;
		for (let i = 0; i < missing.length; i++) {
			this.status = `Geocoding ${i + 1} / ${missing.length}…`;
			this.cdr.markForCheck();
			const done = await this.geocodeViaGoogle(missing[i]);
			done ? ok++ : failed++;
			// Stay under the Maps JS geocoder rate limit.
			await new Promise((r) => setTimeout(r, 220));
		}
		this.geocoding = false;
		this.status = `Geocoded ${ok}, ${failed} failed.`;
		this.refreshMap();
		this.cdr.markForCheck();
	}
	private bulkGeocodeViaServer(missing: SalesRouteStop[]): void {
		this.geocoding = true; this.status = `Geocoding ${missing.length}…`;
		this.reportsService.postSalesRouteBulkGeocode({ items: missing.map((a) => ({ debtorno: a.debtorno, branchcode: a.branchcode })) }).subscribe({
			next: (res: any) => {
				this.geocoding = false;
				if (res?.success) {
					for (const r of (res.results || [])) {
						if (!r.ok) { continue; }
						const a = this.allAccounts.find((x) => x.debtorno === r.debtorno && x.branchcode === r.branchcode);
						if (a) { a.lat = Number(r.lat); a.lng = Number(r.lng); a.needs_geocode = 0; }
					}
					this.status = `Geocoded ${res.succeeded}, ${res.failed} failed.`;
					this.refreshMap();
				}
				this.cdr.markForCheck();
			},
			error: () => { this.geocoding = false; this.status = 'Bulk geocode failed.'; this.cdr.markForCheck(); }
		});
	}

	// ── check-in ────────────────────────────────────────────────────
	checkinGeo: { lat: number; lng: number } | null = null;
	openCheckin(s: SalesRouteStop): void {
		this.checkinStop = s;
		this.checkinOutcome = 'met';
		this.checkinNotes = '';
		this.checkinGeo = null;
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(pos) => { this.checkinGeo = { lat: pos.coords.latitude, lng: pos.coords.longitude }; this.cdr.markForCheck(); },
				() => { /* check-in works without geo */ },
				{ enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
			);
		}
	}
	cancelCheckin(): void { this.checkinStop = null; }
	doCheckin(): void {
		if (!this.checkinStop) { return; }
		const s = this.checkinStop;
		this.checkingIn = true;
		this.reportsService.postSalesRouteCheckin({
			debtorno: s.stop_type === 'prospect' ? '' : s.debtorno,
			branchcode: s.stop_type === 'prospect' ? '' : s.branchcode,
			prospect_key: s.stop_type === 'prospect' ? (s.prospect_key || '') : '',
			salesperson: String(this.salesmanCtrl.value ?? '').trim(),
			outcome: this.checkinOutcome, notes: this.checkinNotes,
			visit_lat: this.checkinGeo?.lat, visit_lng: this.checkinGeo?.lng,
			stop_id: s.stop_id || 0,
		}).subscribe({
			next: (res: any) => {
				this.checkingIn = false;
				if (res?.success) {
					const today = this.fmt(new Date());
					s.visit_count = (s.visit_count || 0) + 1; s.days_since_visit = 0; s.last_visit_date = today;
					if (s.stop_id) { s.stop_status = 'done'; }
					const a = this.allAccounts.find((x) => x.debtorno === s.debtorno && x.branchcode === s.branchcode);
					if (a) { a.visit_count = (a.visit_count || 0) + 1; a.days_since_visit = 0; a.last_visit_date = today; }
					this.status = `Checked in: ${s.name}.`;
				} else { this.status = (res?.message || 'Check-in failed.'); }
				this.checkinStop = null;
				this.cdr.markForCheck();
			},
			error: () => { this.checkingIn = false; this.status = 'Check-in failed.'; this.checkinStop = null; this.cdr.markForCheck(); }
		});
	}

	// ── Customer 360 modal ──────────────────────────────────────────
	c360: SalesRouteStop | null = null;   // the account/lead that was opened
	c360Detail: any = null;               // server detail payload (customers)
	c360Loading = false;
	openCustomer360(s: SalesRouteStop): void {
		this.c360 = s;
		this.c360Detail = null;
		if (s.stop_type === 'prospect') { this.c360Loading = false; return; }
		this.c360Loading = true;
		this.reportsService.postSalesRouteCustomerDetail({ debtorno: s.debtorno, branchcode: s.branchcode }).subscribe({
			next: (res: any) => { this.c360Loading = false; this.c360Detail = res?.success ? res : null; this.cdr.markForCheck(); },
			error: () => { this.c360Loading = false; this.cdr.markForCheck(); }
		});
	}
	closeCustomer360(): void { this.c360 = null; this.c360Detail = null; }
	/** Open the 360 modal for a lead (template-friendly wrapper). */
	openLead(p: ProspectPin): void { this.openCustomer360(this.stopFromProspect(p)); }

	/**
	 * Turn a lead into a real customer using the proven Salon-Prospect → Convert
	 * prefill (customers/create reads router state.salonProspect). Auto-save has
	 * already flushed the week, so the rep's route is safe — the lead stays in
	 * the list until it matches a real account, so nothing is "lost".
	 */
	c360CreateCustomer(): void {
		if (!this.c360 || this.c360.stop_type !== 'prospect') { return; }
		const s = this.c360;
		this.closeCustomer360();
		this.router.navigate(['/customers/create'], {
			state: { salonProspect: {
				name: s.name, address1: s.address, city: s.city, state: s.state || 'NY', zip: s.zip,
				owner: s.owner || '',
			} }
		});
	}

	// ── AI backfill (enrich a salon/customer from the web) ───────────
	c360Enriching = false;
	c360Enrich: any = null;
	c360EnrichMsg = '';
	c360Backfill(): void {
		if (!this.c360) { return; }
		const s = this.c360;
		this.c360Enriching = true; this.c360Enrich = null; this.c360EnrichMsg = '';
		this.reportsService.postSalesRouteAiEnrich({
			name: s.name, address: s.address, city: s.city, state: s.state || 'NY', zip: s.zip,
			debtorno: s.debtorno || '', branchcode: s.branchcode || '', prospect_key: s.prospect_key || '',
		}).subscribe({
			next: (res: any) => {
				this.c360Enriching = false;
				if (res?.success) { this.c360Enrich = res.info || res; }
				else { this.c360EnrichMsg = res?.message || 'AI backfill is not available on this server yet.'; }
				this.cdr.markForCheck();
			},
			error: () => { this.c360Enriching = false; this.c360EnrichMsg = 'AI backfill failed.'; this.cdr.markForCheck(); }
		});
	}

	// ── per-day opportunity stats ───────────────────────────────────
	/** Count of prospect (new-lead) stops on a day. */
	dayLeadCount(stops: SalesRouteStop[]): number { return stops.filter((s) => s.stop_type === 'prospect').length; }
	/** Customers on a day that are due a visit (30+ days / never). */
	dayDueCount(stops: SalesRouteStop[]): number {
		return stops.filter((s) => s.stop_type !== 'prospect' && this.isDueVisit(s)).length;
	}
	/** Estimated annual $ opportunity from the leads planned that day. */
	dayOpportunity(stops: SalesRouteStop[]): number {
		return stops.reduce((n, s) => n + (s.stop_type === 'prospect' && s.est_potential ? Number(s.est_potential) : 0), 0);
	}
	weekOpportunity(): number {
		return this.plannerDays.reduce((n, d) => {
			const wk = this.weeks[d.weekStart];
			return n + (wk ? this.dayOpportunity(wk[d.dow] || []) : 0);
		}, 0);
	}
	/** Add whatever the modal is showing to the active day. */
	c360Add(): void {
		if (!this.c360) { return; }
		if (this.c360.stop_type === 'prospect') {
			this.addProspectToRoute(this.prospectFromStop(this.c360));
		} else { this.addToRoute(this.c360); }
	}
	c360OnDay(): string { return this.c360 ? this.onDayLabelForKey(this.stopKey(this.c360)) : ''; }
	c360Notes(): void { if (this.c360) { const s = this.c360; this.closeCustomer360(); this.openCrm(s); } }
	c360Checkin(): void { if (this.c360) { const s = this.c360; this.closeCustomer360(); this.openCheckin(s); } }
	c360Directions(): void {
		if (!this.c360 || !this.hasCoord(this.c360)) { this.status = 'No GPS on this stop yet.'; return; }
		window.open(`https://www.google.com/maps/dir/?api=1&destination=${this.c360.lat},${this.c360.lng}`, '_blank');
	}
	/** Jump to Order Entry pre-loaded for this customer/branch. */
	c360CreateOrder(): void {
		if (!this.c360 || this.c360.stop_type === 'prospect') { return; }
		const branch = this.c360.branchcode || '';
		this.router.navigate(['/orders/entry/' + this.c360.debtorno + '/' + branch]);
	}
	private prospectFromStop(s: SalesRouteStop): ProspectPin {
		return {
			prospect_key: s.prospect_key || '', business_name: s.name, owner: s.owner || '', license_type: s.license_type || '',
			address: s.address, city: s.city, zip5: s.zip, lat: s.lat, lng: s.lng,
			ops_at_addr: s.ops_at_addr ?? 1, est_potential: s.est_potential ?? null, est_twin: s.est_twin || '',
			rep_name: '', assigned: s.assigned ?? false,
		};
	}

	// ── CRM drawer ──────────────────────────────────────────────────
	openCrm(s: SalesRouteStop): void {
		this.crmTarget = s.stop_type === 'prospect'
			? { prospect_key: s.prospect_key || '', name: s.name }
			: { debtorno: s.debtorno, branchcode: s.branchcode, name: s.name };
	}
	closeCrm(): void { this.crmTarget = null; }

	// ── live route mode ─────────────────────────────────────────────
	startDay(): void {
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		if (!sm) { this.status = 'Pick a salesperson first.'; return; }
		this.router.navigate(['/customers/sales-route-runner'], {
			queryParams: { salesman: sm, week: this.weekStart, dow: this.activeDow }
		});
	}

	// ── AI plan ─────────────────────────────────────────────────────
	openAi(): void { this.showAi = true; this.aiResult = null; this.aiAdvanced = false; }
	closeAi(): void { this.showAi = false; }
	/** One-tap pace → the min/target/max the planner actually uses. */
	setAiPace(pace: 'light' | 'normal' | 'full'): void {
		this.aiPace = pace;
		if (pace === 'light') { this.aiMin = 3; this.aiTarget = 5; this.aiMax = 7; }
		else if (pace === 'full') { this.aiMin = 8; this.aiTarget = 11; this.aiMax = 14; }
		else { this.aiMin = 5; this.aiTarget = 8; this.aiMax = 12; }
	}
	aiSelectedDayCount(): number { return this.days.filter((d) => this.aiDays[d.full]).length; }
	runAi(): void {
		const geocoded = this.allAccounts.filter((a) => !a.needs_geocode && Number.isFinite(a.lat));
		if (!geocoded.length) { this.status = 'No geocoded accounts to plan.'; return; }
		const daysAvailable = this.days.filter((d) => this.aiDays[d.full]).map((d) => d.full);
		this.planningAi = true; this.aiResult = null;
		this.reportsService.postSalesRouteAiplan({
			accounts: geocoded.map((a) => ({
				debtorno: a.debtorno, branchcode: a.branchcode, name: a.name, city: a.city, state: a.state,
				lat: a.lat, lng: a.lng, priority: a.priority ?? 2, days_since_visit: a.days_since_visit,
			})),
			home_base: { label: this.homeBaseLabel, lat: this.homeBaseLat, lng: this.homeBaseLng },
			end: { mode: this.endMode },
			constraints: { days_available: daysAvailable, stops_per_day: { min: this.aiMin, target: this.aiTarget, max: this.aiMax }, extra_notes: this.aiNotes },
		}).subscribe({
			next: (res: any) => {
				this.planningAi = false;
				if (res?.success) { this.aiResult = res; }
				else { this.status = res?.message || 'AI planning failed.'; }
				this.cdr.markForCheck();
			},
			error: () => { this.planningAi = false; this.status = 'AI planning failed.'; this.cdr.markForCheck(); }
		});
	}
	applyAiPlan(): void {
		if (!this.aiResult?.days) { return; }
		this.snapshotForUndo('AI plan');
		const byId = new Map(this.allAccounts.map((a) => [`${a.debtorno}|${a.branchcode}`, a]));
		// The AI planner only places customers. Keep any leads the rep already
		// added to each day so "Use this plan" never silently drops them.
		const keptLeads: Record<number, SalesRouteStop[]> = { 0: [], 1: [], 2: [], 3: [], 4: [] };
		let leadCount = 0;
		for (const dow of [0, 1, 2, 3, 4]) {
			for (const s of (this.routesByDay[dow] || [])) {
				if (s.stop_type === 'prospect') { keptLeads[dow].push(s); leadCount++; }
			}
		}
		const fresh: Record<number, SalesRouteStop[]> = { 0: [], 1: [], 2: [], 3: [], 4: [] };
		for (const d of this.days) {
			const ids = (this.aiResult.days[d.full] || []) as string[];
			for (const id of ids) { const a = byId.get(id); if (a) { fresh[d.dow].push({ ...a }); } }
			for (const lead of keptLeads[d.dow]) { fresh[d.dow].push(lead); }
		}
		this.routesByDay = fresh;
		this.showAi = false;
		this.status = leadCount ? `Applied AI plan (kept ${leadCount} lead${leadCount === 1 ? '' : 's'}).` : 'Applied AI plan to the week.';
		this.afterMutate();
	}

	// ── list view (map alternative) ─────────────────────────────────
	listView = false;
	toggleListView(): void {
		this.listView = !this.listView;
		if (!this.listView) {
			// Map was hidden — let it re-measure when it comes back.
			setTimeout(() => { if (this.map && typeof google !== 'undefined') { google.maps.event.trigger(this.map, 'resize'); this.refreshMap(); } }, 150);
		}
	}

	// ── route settings ──────────────────────────────────────────────
	startAddr = '';
	/** Set the day's starting point (home base) from a typed address. Geocode
	 *  client-side via Google (the server geocoder is dead on most hosts). */
	async saveStartAddress(): Promise<void> {
		const addr = this.startAddr.trim();
		if (!addr) { return; }
		this.geocoding = true; this.status = null;
		const geo = await this.googleGeocode(addr);
		this.geocoding = false;
		if (geo && this.inNY(geo.lat, geo.lng)) {
			this.setDayOverride({ home_base_lat: geo.lat, home_base_lng: geo.lng, home_base_label: addr });
			this.startAddr = '';
			this.afterMutate();
		} else {
			this.status = geo ? 'That address is outside New York — check it.' : 'Could not find that address.';
		}
		this.cdr.markForCheck();
	}
	/** Set a custom end point from a typed address. Geocode client-side via
	 *  Google (the server geocoder is dead on most hosts). */
	async saveCustomEnd(): Promise<void> {
		const addr = this.endAddr.trim();
		if (!addr) { return; }
		this.geocoding = true; this.status = null;
		const geo = await this.googleGeocode(addr);
		this.geocoding = false;
		if (geo && this.inNY(geo.lat, geo.lng)) {
			this.setDayOverride({ end_mode: 'custom', end_lat: geo.lat, end_lng: geo.lng, end_label: addr });
			this.endAddr = '';
			this.afterMutate();
		} else {
			this.status = geo ? 'That address is outside New York — check it.' : 'Could not find that address.';
		}
		this.cdr.markForCheck();
	}

	// ── templates ───────────────────────────────────────────────────
	loadTemplates(): void {
		this.loadingTemplates = true;
		const zone = String(this.templateZoneCtrl.value ?? '').trim();
		this.reportsService.getSalesRouteTemplates(zone || undefined).subscribe({
			next: (res: any) => {
				this.loadingTemplates = false;
				this.templates = res?.success ? (res.templates || []) : [];
				this.templateZones = res?.zones || [];
				this.cdr.markForCheck();
			},
			error: () => { this.loadingTemplates = false; this.cdr.markForCheck(); }
		});
	}
	applyTemplate(t: RouteTemplate): void {
		this.applyingTemplate = true; this.status = null;
		this.reportsService.postSalesRouteApplyTemplate({ template_id: t.template_id }).subscribe({
			next: (res: any) => {
				this.applyingTemplate = false;
				if (!res?.success) { this.status = 'Could not apply template.'; this.cdr.markForCheck(); return; }
				this.snapshotForUndo(`apply “${t.route_name}”`);
				const incoming = (res.stops || []).map((o: any) => this.normStop(o)).filter((s: SalesRouteStop) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
				const seen = new Set(this.activeStops.map((s) => this.stopKey(s)));
				const merged = [...this.activeStops];
				for (const s of incoming) { const k = this.stopKey(s); if (!seen.has(k)) { merged.push(s); seen.add(k); } }
				this.routesByDay[this.activeDow] = merged;
				this.status = `Applied ${incoming.length} stop(s) to ${this.days[this.activeDow].label}${res.skipped_count ? ` (${res.skipped_count} unmatched skipped)` : ''}.`;
				this.afterMutate();
			},
			error: () => { this.applyingTemplate = false; this.status = 'Could not apply template.'; this.cdr.markForCheck(); }
		});
	}

	// ── save / load + draft ─────────────────────────────────────────
	private draftKey(): string { return `srb_draft_${String(this.salesmanCtrl.value ?? '')}_${this.weekStart}`; }
	private persistDraftSoon(): void {
		if (this.draftTimer) { clearTimeout(this.draftTimer); }
		this.draftTimer = setTimeout(() => this.persistDraft(), 600);
	}
	private persistDraft(): void {
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		if (!sm || !this.totalStops()) { return; }
		try { localStorage.setItem(this.draftKey(), JSON.stringify({ routesByDay: this.routesByDay, savedAt: new Date().toISOString() })); } catch (_) { /* quota */ }
	}
	private clearDraft(): void { try { localStorage.removeItem(this.draftKey()); } catch (_) { /* ignore */ } }
	discardDraft(): void { this.draftRestored = false; this.clearDraft(); this.loadWeek(true); }
	keepDraft(): void { this.draftRestored = false; }

	/** Load every week the rolling strip spans (1 or 2). */
	loadWeek(skipDraft = false): void {
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		if (!sm || !this.plannerDays.length) { return; }
		this.weeks = {};
		this.dirtyWeeks.clear();
		this.unsaved = false;
		this.undoSnap = null;   // snapshot would be stale against fresh server data
		this.reportsService.getSalesRouteList(sm).subscribe({
			next: (res: any) => {
				this.savedWeeks = res?.success ? (res.routes || []) : [];
				this.lastWeekHadPlan = this.savedWeeks.some((w) => w.week_start === this.prevWeekStart());
				this.cdr.markForCheck();
			},
			error: () => { /* non-fatal */ }
		});
		for (const ws of this.windowWeeks()) {
			this.loadWeekData(ws, !skipDraft && ws === this.activeWeekStart);
		}
	}
	/** Fetch one week's stops into the weeks map. */
	private loadWeekData(ws: string, restoreDraft = false): void {
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		if (!sm) { return; }
		this.loadingWeek = true;
		this.reportsService.getSalesRouteWeek(sm, ws).subscribe({
			next: (res: any) => {
				this.loadingWeek = false;
				const fresh: Record<number, SalesRouteStop[]> = { 0: [], 1: [], 2: [], 3: [], 4: [] };
				if (res?.success && Array.isArray(res.stops)) {
					for (const o of res.stops) { const dow = Number(o.day_of_week); if (fresh[dow]) { fresh[dow].push(this.normStop(o)); } }
				}
				this.weeks[ws] = fresh;
				this.dirtyWeeks.delete(ws);
				this.backfillProspectStops();
				this.fpCacheKey = '';
				this.nearbyCacheKey = '';
				this.restoreDayPoints(ws, res?.route);
				if (ws === this.activeWeekStart) {
					this.lastOptimizeMiles = null;
					this.restoreRouteSettings(res?.route);
					if (restoreDraft) { this.maybeRestoreDraft(); }
				}
				this.refreshMap();
				this.cdr.markForCheck();
			},
			error: () => { this.loadingWeek = false; this.cdr.markForCheck(); }
		});
	}
	/** Restore the saved start/end points for this week so custom points persist. */
	private restoreRouteSettings(route: any): void {
		if (!route) { return; }
		const lat = this.num(route.home_base_lat), lng = this.num(route.home_base_lng);
		if (lat != null && lng != null) {
			this.homeBaseLat = lat; this.homeBaseLng = lng;
			if (route.home_base_label) { this.homeBaseLabel = String(route.home_base_label); }
		}
		const em = String(route.end_mode ?? '');
		if (em === 'home' || em === 'last' || em === 'custom') { this.endMode = em; }
		const elat = this.num(route.end_lat), elng = this.num(route.end_lng);
		this.endLat = elat; this.endLng = elng;
		this.endLabel = String(route.end_label ?? '');
	}
	/** Populate this WEEK's own default point + any per-day overrides, keyed by
	 *  its own weekStart — called for every loaded week (not just the active one)
	 *  so cross-week mileage (weekMiles/printRoute) never borrows another week's
	 *  start/end by mistake. See migrations/sales_route_day_points.sql. */
	private restoreDayPoints(ws: string, route: any): void {
		const em = String(route?.end_mode ?? '');
		const wkDefault: DayPoint = {
			home_base_lat: this.num(route?.home_base_lat), home_base_lng: this.num(route?.home_base_lng),
			home_base_label: route?.home_base_label ? String(route.home_base_label) : '',
			end_mode: (em === 'last' || em === 'custom') ? em : 'home',
			end_lat: this.num(route?.end_lat), end_lng: this.num(route?.end_lng),
			end_label: route?.end_label ? String(route.end_label) : '',
		};
		this.weekDefaults[ws] = wkDefault;
		const overrides: Record<number, DayPoint> = {};
		const dp = route?.day_points || {};
		for (const key of Object.keys(dp)) {
			const row = dp[key] || {};
			const rowEm = String(row.end_mode ?? '');
			overrides[Number(key)] = {
				home_base_lat: this.num(row.home_base_lat) ?? wkDefault.home_base_lat,
				home_base_lng: this.num(row.home_base_lng) ?? wkDefault.home_base_lng,
				home_base_label: row.home_base_label ? String(row.home_base_label) : wkDefault.home_base_label,
				end_mode: (rowEm === 'home' || rowEm === 'last' || rowEm === 'custom') ? rowEm : wkDefault.end_mode,
				end_lat: this.num(row.end_lat) ?? wkDefault.end_lat, end_lng: this.num(row.end_lng) ?? wkDefault.end_lng,
				end_label: row.end_label ? String(row.end_label) : wkDefault.end_label,
			};
		}
		this.dayPointOverrides[ws] = overrides;
	}
	private maybeRestoreDraft(): void {
		try {
			const raw = localStorage.getItem(this.draftKey());
			if (!raw) { return; }
			const draft = JSON.parse(raw);
			if (draft?.routesByDay) {
				const draftCount = Object.values(draft.routesByDay as Record<number, any[]>).reduce((n, a) => n + (a?.length || 0), 0);
				if (draftCount && draftCount !== this.totalStops()) { this.routesByDay = draft.routesByDay; this.draftRestored = true; }
			}
		} catch (_) { /* ignore */ }
	}

	/** Build the per-day start/end override payload for one week — a day absent
	 *  here (null) tells the server to delete any existing override, reverting
	 *  that day back to the week's shared default. */
	private buildDayPoints(ws: string): Record<number, DayPoint | null> {
		const overrides = this.dayPointOverrides[ws] || {};
		const out: Record<number, DayPoint | null> = {};
		for (let dow = 0; dow <= 4; dow++) { out[dow] = overrides[dow] || null; }
		return out;
	}
	/** Build the server stop payload for one loaded week. */
	private buildStops(ws: string): any[] {
		const wk = this.weeks[ws];
		const stops: any[] = [];
		if (!wk) { return stops; }
		for (let dow = 0; dow < 5; dow++) {
			(wk[dow] || []).forEach((s, position) => {
				stops.push({
					day_of_week: dow, position, debtorno: s.debtorno, branchcode: s.branchcode, priority: s.priority ?? 2,
					stop_type: s.stop_type === 'prospect' ? 'prospect' : 'customer',
					prospect_key: s.prospect_key || '',
					prospect_name: s.stop_type === 'prospect' ? s.name : '',
					prospect_address: s.stop_type === 'prospect' ? [s.address, s.city].filter(Boolean).join(', ') : '',
					prospect_lat: s.stop_type === 'prospect' ? s.lat : null,
					prospect_lng: s.stop_type === 'prospect' ? s.lng : null,
				});
			});
		}
		return stops;
	}

	/** Save every week with unsaved edits (the strip can touch two). */
	saveWeek(silent = false): void {
		if (this.autoSaveTimer) { clearTimeout(this.autoSaveTimer); this.autoSaveTimer = null; }
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		if (!sm) { if (!silent) { this.status = 'Pick a salesperson first.'; } return; }

		const weeksToSave = this.dirtyWeeks.size ? Array.from(this.dirtyWeeks) : [this.activeWeekStart];
		if (!weeksToSave.length) { return; }
		this.saving = true;
		if (!silent) { this.status = null; }
		let remaining = weeksToSave.length;
		let ok = true;
		for (const ws of weeksToSave) {
			this.reportsService.postSalesRouteSave({
				userid: this.authUserid(), salesperson: sm, week_start: ws,
				home_base_lat: this.homeBaseLat, home_base_lng: this.homeBaseLng, home_base_label: this.homeBaseLabel,
				end_mode: this.endMode, end_lat: this.endLat, end_lng: this.endLng, end_label: this.endLabel,
				day_points: this.buildDayPoints(ws),
				stops: this.buildStops(ws),
			}).subscribe({
				next: (res: any) => {
					if (res?.success) { this.dirtyWeeks.delete(ws); } else { ok = false; }
					this.done(--remaining, ok, silent);
				},
				error: () => { ok = false; this.done(--remaining, ok, silent); }
			});
		}
	}
	/** Finish a (possibly multi-week) save once all requests return. */
	private done(remaining: number, ok: boolean, silent: boolean): void {
		if (remaining > 0) { return; }
		this.saving = false;
		this.unsaved = this.dirtyWeeks.size > 0;
		if (ok && !this.unsaved) {
			this.draftRestored = false;
			this.clearDraft();
			if (!silent) { this.status = `Saved — ${this.totalStops()} stop(s) planned.`; }
		} else if (!silent) {
			// Failed saves leave the week dirty so the chip keeps warning.
			this.status = 'Could not save everything — tap Saved to retry.';
		}
		this.cdr.markForCheck();
	}

	// ── export ──────────────────────────────────────────────────────
	openInMaps(): void {
		const pts = this.activeStops.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng)).slice(0, 10);
		if (!pts.length) { return; }
		// Honor the chosen start and end: origin = start point; destination =
		// home (round trip), custom end, or the last stop (one-way).
		const origin = this.effHomeBaseLat != null ? `${this.effHomeBaseLat},${this.effHomeBaseLng}` : `${pts[0].lat},${pts[0].lng}`;
		let dest: string;
		let waypts = pts;
		if (this.effEndMode === 'home' && this.effHomeBaseLat != null) {
			dest = `${this.effHomeBaseLat},${this.effHomeBaseLng}`;
		} else if (this.effEndMode === 'custom' && this.effEndLat != null && this.effEndLng != null) {
			dest = `${this.effEndLat},${this.effEndLng}`;
		} else {
			dest = `${pts[pts.length - 1].lat},${pts[pts.length - 1].lng}`;
			waypts = pts.slice(0, -1);
		}
		const way = waypts.map((p) => `${p.lat},${p.lng}`).join('|');
		const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}` + (way ? `&waypoints=${encodeURIComponent(way)}` : '');
		window.open(url, '_blank');
	}
	/** Open a clean, printable route sheet for the whole rolling window —
	 *  not window.print() of the on-screen map. */
	printRoute(): void {
		const rep = (this.repName || String(this.salesmanCtrl.value ?? '') || '').trim();
		const esc = (s: any) => this.escHtml(String(s ?? ''));
		let body = '';
		let grandStops = 0, grandMiles = 0;
		for (const d of this.plannerDays) {
			const wk = this.weeks[d.weekStart];
			const stops = wk ? (wk[d.dow] || []) : [];
			if (!stops.length) { continue; }
			const miles = this.dayMiles(stops);
			grandStops += stops.length; grandMiles += miles;
			const rows = stops.map((s, i) => `
				<tr>
					<td class="n">${i + 1}</td>
					<td>${esc(s.name || s.brname)}${s.stop_type === 'prospect' ? ' <span class="lead">LEAD</span>' : ''}</td>
					<td>${esc([s.address, s.city, s.zip].filter(Boolean).join(', '))}</td>
					<td>${esc(s.phoneno || '')}</td>
					<td class="pr">${this.priorityLabel(s.priority)}</td>
					<td class="chk"></td>
				</tr>`).join('');
			body += `
				<section>
					<h2>${esc(d.full)} <span class="date">${esc(d.dateShort)}</span>
						<span class="meta">${stops.length} stop${stops.length === 1 ? '' : 's'} · ~${Math.round(miles)} mi</span></h2>
					<table>
						<thead><tr><th class="n">#</th><th>Customer</th><th>Address</th><th>Phone</th><th class="pr">Pri</th><th class="chk">Done</th></tr></thead>
						<tbody>${rows}</tbody>
					</table>
				</section>`;
		}
		if (!grandStops) { this.status = 'Nothing to print — add some stops first.'; return; }
		const doc = `<!doctype html><html><head><meta charset="utf-8"><title>Sales route — ${esc(rep)}</title>
			<style>
				* { box-sizing: border-box; }
				body { font-family: -apple-system, Arial, sans-serif; color: #1a2230; margin: 24px; }
				h1 { font-size: 20px; margin: 0 0 2px; }
				.sub { color: #667; font-size: 13px; margin-bottom: 16px; }
				section { margin-bottom: 18px; page-break-inside: avoid; }
				h2 { font-size: 15px; border-bottom: 2px solid #1e88e5; padding-bottom: 4px; margin: 0 0 6px; }
				h2 .date { color: #90a4ae; font-weight: 400; }
				h2 .meta { float: right; font-size: 12px; color: #667; font-weight: 400; }
				table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
				th { text-align: left; color: #667; font-size: 10.5px; text-transform: uppercase; border-bottom: 1px solid #cfd8dc; padding: 3px 6px; }
				td { padding: 5px 6px; border-bottom: 1px solid #eef1f4; vertical-align: top; }
				td.n, th.n { width: 26px; text-align: center; color: #90a4ae; }
				td.pr, th.pr { width: 40px; }
				td.chk, th.chk { width: 44px; }
				td.chk { border: 1px solid #cfd8dc; border-bottom: 1px solid #cfd8dc; }
				.lead { background: #fff3e0; color: #e65100; font-size: 9px; font-weight: 700; padding: 0 4px; border-radius: 3px; vertical-align: 1px; }
				.total { margin-top: 14px; font-weight: 700; border-top: 2px solid #1a2230; padding-top: 6px; }
				@media print { body { margin: 0.4in; } }
			</style></head>
			<body>
				<h1>Sales route${rep ? ' — ' + esc(rep) : ''}</h1>
				<div class="sub">${esc(this.weekLabel())}</div>
				${body}
				<div class="total">Total: ${grandStops} stops · about ${Math.round(grandMiles)} miles</div>
			</body></html>`;
		const w = window.open('', '_blank');
		if (!w) { this.status = 'Allow pop-ups to print your route sheet.'; return; }
		w.document.open(); w.document.write(doc); w.document.close();
		w.focus();
		setTimeout(() => { try { w.print(); } catch (_) { /* user can print manually */ } }, 350);
	}

	// ── Phase 4: recenter, save-as-template, territory manager ──────
	fullMap = false;
	toggleFullMap(): void {
		this.fullMap = !this.fullMap;
		// Let the DOM resize, then tell Google Maps to re-measure + recenter.
		setTimeout(() => {
			if (this.map && typeof google !== 'undefined') {
				google.maps.event.trigger(this.map, 'resize');
				this.refreshMap();
			}
		}, 150);
	}

	recenterMap(): void {
		if (!navigator.geolocation || !this.map) { this.status = 'Geolocation unavailable.'; return; }
		navigator.geolocation.getCurrentPosition(
			(pos) => { this.map.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }); this.map.setZoom(12); },
			() => { this.status = 'Could not get your location.'; this.cdr.markForCheck(); },
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	}

	openTemplateSave(): void { this.tmplZone = ''; this.tmplName = ''; this.showTemplateSave = true; }
	closeTemplateSave(): void { this.showTemplateSave = false; }
	saveAsTemplate(): void {
		const stops = this.activeStops.filter((s) => s.debtorno && s.branchcode)
			.map((s) => ({ debtorno: s.debtorno, branchcode: s.branchcode, name: s.name, address: s.address, city: s.city, zip: s.zip }));
		if (!this.tmplZone.trim() || !this.tmplName.trim() || !stops.length) { this.status = 'Zone, name and at least one stop required.'; return; }
		this.reportsService.postSalesRouteSaveTemplate({ zone: this.tmplZone.trim(), route_name: this.tmplName.trim(), stops }).subscribe({
			next: (res: any) => {
				this.showTemplateSave = false;
				this.status = res?.success ? `Saved template “${this.tmplName.trim()}” (${res.stop_count} stops).` : 'Template save failed.';
				this.loadTemplates();
				this.cdr.markForCheck();
			},
			error: () => { this.status = 'Template save failed.'; this.cdr.markForCheck(); }
		});
	}

	openTerritoryMgr(): void { this.showTerritoryMgr = true; this.tmgrId = null; this.tmgrBranches = []; this.tmgrSearch = ''; this.tmgrTotal = 0; this.tmgrSearched = false; }
	closeTerritoryMgr(): void { this.showTerritoryMgr = false; if (this.tmgrSearchTimer) { clearTimeout(this.tmgrSearchTimer); } }
	/**
	 * A "territory" here is often a single catch-all default covering the
	 * entire customer base (AA's only territory has 33,500+ branches) —
	 * loading everything crashed the modal in practice. Requires a search
	 * term before fetching anything, and the server caps results at 200.
	 */
	loadTerritoryBranches(tid: number): void {
		this.tmgrId = tid; this.tmgrBranches = []; this.tmgrSearch = ''; this.tmgrTotal = 0; this.tmgrSearched = false;
		this.reportsService.getSalesRouteTerritoryBranches(tid).subscribe({
			next: (res: any) => { this.tmgrTotal = Number(res?.total ?? 0); this.cdr.markForCheck(); },
			error: () => { this.cdr.markForCheck(); }
		});
	}
	onTmgrSearch(): void {
		if (this.tmgrSearchTimer) { clearTimeout(this.tmgrSearchTimer); }
		this.tmgrSearchTimer = setTimeout(() => this.runTmgrSearch(), 300);
	}
	private runTmgrSearch(): void {
		if (!this.tmgrId) { return; }
		const q = this.tmgrSearch.trim();
		if (!q) { this.tmgrBranches = []; this.tmgrSearched = false; this.cdr.markForCheck(); return; }
		this.tmgrLoading = true; this.tmgrSearched = true;
		this.reportsService.getSalesRouteTerritoryBranches(this.tmgrId, q).subscribe({
			next: (res: any) => { this.tmgrBranches = res?.success ? (res.branches || []) : []; this.tmgrLoading = false; this.cdr.markForCheck(); },
			error: () => { this.tmgrLoading = false; this.cdr.markForCheck(); }
		});
	}
	assignBranch(b: any, tid: any): void {
		const t = Number(tid);
		if (!t || !b) { return; }
		this.reportsService.postSalesRouteAssignTerritory({ debtorno: b.debtorno, branchcode: b.branchcode, territory_id: t }).subscribe({
			next: (res: any) => {
				if (res?.success && this.tmgrId && t !== this.tmgrId) {
					this.tmgrBranches = this.tmgrBranches.filter((x) => !(x.debtorno === b.debtorno && x.branchcode === b.branchcode));
				}
				this.cdr.markForCheck();
			},
			error: () => { this.cdr.markForCheck(); }
		});
	}

	// ── prospect layer (beautician prospecting) ─────────────────────
	toggleProspects(): void {
		this.showProspects = !this.showProspects;
		if (this.showProspects && !this.prospects.length) { this.loadProspects(); return; }
		this.repaintProspects();
	}
	loadProspects(): void {
		this.loadingProspects = true;
		const sm = String(this.salesmanCtrl.value ?? '').trim();
		this.reportsService.postSalesRouteProspects({
			salesman: sm, scope: sm ? 'mine' : 'all', limit: 2000,
		}).subscribe({
			next: (res: any) => {
				this.loadingProspects = false;
				this.prospectsAvailable = res?.available !== false;
				this.prospectTotal = Number(res?.total ?? 0);
				this.prospects = (res?.success ? (res.prospects || []) : []).map((p: any): ProspectPin => ({
					// Guarantee a unique key: without one, every keyless lead shares
					// `p|` and only the first can ever be added to a day.
					prospect_key: String(p.prospect_key ?? '').trim()
						|| `syn:${String(p.business_name ?? '')}|${String(p.address ?? '')}|${String(p.zip5 ?? '')}`.toLowerCase().replace(/\s+/g, ' ').trim(),
					business_name: String(p.business_name ?? ''),
					owner: String(p.owner ?? ''),
					license_type: String(p.license_type ?? ''),
					address: String(p.address ?? ''),
					city: String(p.city ?? ''),
					zip5: String(p.zip5 ?? ''),
					lat: Number(p.lat), lng: Number(p.lng),
					ops_at_addr: Number(p.ops_at_addr ?? 1),
					est_potential: p.est_potential != null ? Number(p.est_potential) : null,
					est_twin: String(p.est_twin ?? ''),
					rep_name: String(p.rep_name ?? ''),
					assigned: Number(p.assigned ?? 0) === 1,
				}));
				this.fpCacheKey = '';
				this.nearbyCacheKey = '';
				this.backfillProspectStops();
				// A rep with a manager-assigned list starts on it — that's their homework.
				if (this.isRep && this.assignedCount()) { this.leadFilter = 'assigned'; }
				if (!this.prospectsAvailable) { this.status = 'Leads are not turned on for this company.'; this.showProspects = false; }
				this.repaintProspects();
				this.cdr.markForCheck();
			},
			error: () => { this.loadingProspects = false; this.status = 'Could not load prospects.'; this.cdr.markForCheck(); }
		});
	}
	private clearProspectMarkers(): void {
		for (const m of this.prospectMarkers) { m.setMap(null); }
		this.prospectMarkers = [];
	}
	private closeInfoWin(): void {
		if (this.prospectInfoWin) { this.prospectInfoWin.close(); this.prospectInfoWin = null; }
	}
	private escHtml(s: string): string {
		return String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
	}
	private pwButton(label: string, primary: boolean, onClick: () => void): HTMLButtonElement {
		const b = document.createElement('button');
		b.textContent = label;
		b.style.cssText = primary
			? 'background:#1e88e5;color:#fff;border:0;border-radius:6px;padding:6px 12px;font-weight:600;cursor:pointer;margin:6px 6px 0 0'
			: 'background:#fff;color:#455a64;border:1px solid #cfd8dc;border-radius:6px;padding:6px 12px;font-weight:600;cursor:pointer;margin:6px 6px 0 0';
		b.addEventListener('click', onClick);
		return b;
	}
	/** One shared info window, opened at a position (survives pool re-clustering). */
	private openInfoAt(pos: { lat: number; lng: number }, content: HTMLElement): void {
		this.closeInfoWin();
		this.prospectInfoWin = new google.maps.InfoWindow({ content, position: pos });
		this.prospectInfoWin.open({ map: this.map });
	}

	// ── lightweight hover peek ──────────────────────────────────────────────
	// A Google InfoWindow re-opening on every mouseover pans/flickers the map
	// ("address jumpy on hover"). A native marker title tooltip never moves the
	// map and never thrashes, so hover peeks use that instead.
	private hideHover(): void { /* native tooltips need no teardown */ }
	private stripTags(html: string): string {
		return String(html || '').replace(/<br\s*\/?>/gi, ' — ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
	}
	private attachHover(marker: any, _key: string, _pos: { lat: number; lng: number }, html: string): void {
		marker.setTitle(this.stripTags(html));
	}
	private accountTip(a: SalesRouteStop): string {
		const badge = this.visitBadge(a);
		return `<strong>${this.escHtml(this.accountLabel(a))}</strong>`
			+ (this.stopAddress(a) ? `<br><span>${this.escHtml(this.stopAddress(a))}</span>` : '')
			+ (badge ? `<br><span class="${this.isDueVisit(a) ? 'due' : ''}">${this.escHtml(badge)}</span>` : '');
	}
	private repaintProspects(): void {
		this.clearProspectMarkers();
		if (!this.map || typeof google === 'undefined') { return; }
		// Paint leads when their layer is on, OR on the Nearby tab (which shows
		// leads close to the day's stops even if the Leads layer is off).
		const nearbyLeads = this.sideTab === 'nearby' && this.nearbyIncludeLeads;
		if (!this.showProspects && !nearbyLeads) { return; }
		const nearbyKeys = this.sideTab === 'nearby' ? this.nearbyKeySet() : null;
		// Paint only what the left panel is showing — the search/filter/radius the
		// rep set. Fewer pins on screen, and the map stays in sync with the list.
		for (const p of this.matchingProspects()) {
			if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) { continue; }
			if (nearbyKeys && !nearbyKeys.has(`p|${p.prospect_key}`)) { continue; }
			const marker = new google.maps.Marker({
				position: { lat: p.lat, lng: p.lng }, map: this.map,
				title: `${p.business_name || p.owner}${p.assigned ? ' — assigned to me' : ' (lead)'}`,
				icon: {
					path: google.maps.SymbolPath.CIRCLE,
					scale: (p.assigned ? 1.5 : 0) + (p.est_potential ? Math.min(10, 5.5 + Math.sqrt(p.est_potential) / 40) : 5.5),
					fillColor: p.assigned ? '#8e24aa' : '#fb8c00', fillOpacity: 0.95,
					strokeColor: '#fff', strokeWeight: p.assigned ? 2.5 : 1.5,
				},
				zIndex: p.assigned ? 500 : 100,
			});
			const tip = `<strong>${this.escHtml(p.business_name || p.owner)}</strong>`
				+ (p.assigned ? ' <span class="tip-assigned">assigned</span>' : '')
				+ (p.city ? `<br><span>${this.escHtml([p.address, p.city].filter(Boolean).join(', '))}</span>` : '')
				+ `<br><span>${this.escHtml(this.licenseLabel(p.license_type))}`
				+ (p.est_potential ? ` · ~$${Number(p.est_potential).toLocaleString()}/yr` : '')
				+ ` · ${this.leadDistance(p).toFixed(1)} mi</span>`;
			this.attachHover(marker, `p|${p.prospect_key}`, { lat: p.lat, lng: p.lng }, tip);
			marker.addListener('click', () => { if (this.consumeSetPointClick(p.lat, p.lng)) { return; } this.openCustomer360(this.stopFromProspect(p)); });
			this.prospectMarkers.push(marker);
		}
	}
	/** A lead rendered as a stop, so the same 360 modal can show it. */
	private stopFromProspect(p: ProspectPin): SalesRouteStop {
		return {
			debtorno: '', branchcode: '', name: p.business_name || p.owner, brname: '',
			address: p.address, city: p.city, state: 'NY', zip: p.zip5,
			lat: p.lat, lng: p.lng, salesman: '', territory_id: '',
			priority: 2, needs_geocode: 0, visit_count: 0, days_since_visit: null, last_visit_date: null,
			stop_type: 'prospect', prospect_key: p.prospect_key, stop_status: 'pending',
			// carry lead-only facts for the modal / convert prefill
			...( { est_potential: p.est_potential, license_type: p.license_type, ops_at_addr: p.ops_at_addr, est_twin: p.est_twin, assigned: p.assigned, owner: p.owner } as any ),
		} as SalesRouteStop;
	}

	/** Several customers stacked at one spot — let the rep pick which one. */
	private openClusterPicker(bucket: SalesRouteStop[], center: { lat: number; lng: number }): void {
		const wrap = document.createElement('div');
		wrap.className = 'srb-pw srb-pw-picker';
		const head = document.createElement('div');
		head.className = 'srb-pw-pickhd';
		head.innerHTML = `<strong>${bucket.length} customers here</strong>`;
		wrap.appendChild(head);
		const list = document.createElement('div');
		list.className = 'srb-pw-picklist';
		for (const a of bucket.slice().sort((x, y) => this.accountLabel(x).localeCompare(this.accountLabel(y)))) {
			const row = document.createElement('div');
			row.className = 'srb-pw-pickrow';
			const info = document.createElement('div');
			info.className = 'srb-pw-pickinfo';
			info.innerHTML = `<span class="nm">${this.escHtml(this.accountLabel(a))}</span>`
				+ (this.stopAddress(a) ? `<span class="ad">${this.escHtml(this.stopAddress(a))}</span>` : '')
				+ `<span class="bd">${this.escHtml(this.visitBadge(a))}</span>`;
			info.style.cursor = 'pointer';
			info.addEventListener('click', () => { this.closeInfoWin(); this.openCustomer360(a); });
			row.appendChild(info);
			if (!this.isOnActiveDay(a)) {
				row.appendChild(this.pwButton(`+ ${this.activeDayLabel()}`, true, () => { this.addToRoute(a); this.closeInfoWin(); this.cdr.markForCheck(); }));
			} else {
				const on = document.createElement('span'); on.className = 'srb-pw-onday'; on.textContent = 'added'; row.appendChild(on);
			}
			list.appendChild(row);
		}
		wrap.appendChild(list);
		this.openInfoAt(center, wrap);
	}

	/** Route stop tapped — check in, notes, or drop it from the day. */
	private openStopInfo(s: SalesRouteStop, idx: number): void {
		const wrap = document.createElement('div');
		wrap.className = 'srb-pw';
		const statusTxt = s.stop_status === 'done' ? ' · ✓ visited' : s.stop_status === 'skipped' ? ' · skipped' : '';
		wrap.innerHTML = `
			<strong>#${idx + 1} ${this.escHtml(this.accountLabel(s))}</strong>${statusTxt}<br>
			<span>${this.escHtml([s.address, s.city].filter(Boolean).join(', '))}</span>
			${s.stop_type === 'prospect' ? '<br><span class="srb-pw-prospect">Prospect</span>' : ''}`;
		wrap.appendChild(this.pwButton('Check in', true, () => { this.openCheckin(s); this.closeInfoWin(); this.cdr.markForCheck(); }));
		wrap.appendChild(this.pwButton('Notes', false, () => { this.openCrm(s); this.closeInfoWin(); this.cdr.markForCheck(); }));
		wrap.appendChild(this.pwButton('Remove', false, () => { this.removeFromRoute(idx); this.closeInfoWin(); this.cdr.markForCheck(); }));
		this.openInfoAt({ lat: s.lat, lng: s.lng }, wrap);
	}
	addProspectToRoute(p: ProspectPin): void {
		const stop: SalesRouteStop = {
			debtorno: '', branchcode: '',
			name: p.business_name || p.owner, brname: '',
			address: p.address, city: p.city, state: 'NY', zip: p.zip5,
			lat: p.lat, lng: p.lng,
			salesman: '', territory_id: '',
			priority: 2, needs_geocode: 0,
			visit_count: 0, days_since_visit: null, last_visit_date: null,
			stop_type: 'prospect', prospect_key: p.prospect_key, stop_status: 'pending',
			est_potential: p.est_potential, license_type: p.license_type, ops_at_addr: p.ops_at_addr,
			est_twin: p.est_twin, assigned: p.assigned,
		};
		const list = this.routesByDay[this.activeDow];
		const already = this.onDayLabelForKey(this.stopKey(stop));
		if (already) { this.status = `${stop.name} is already on ${already}.`; this.cdr.markForCheck(); return; }
		this.routesByDay[this.activeDow] = [...list, stop];
		this.status = `Added ${stop.name} to ${this.days[this.activeDow].label}.`;
		this.afterMutate();
	}

	// ── map ─────────────────────────────────────────────────────────
	private lastFitDow = -1;
	private lastPathLen = 0;
	private didPoolFit = false;
	private tryInitMap(): void {
		if (typeof google === 'undefined' || !google.maps) { this.mapError = 'Map library is not available.'; return; }
		const el = this.mapHost?.nativeElement || document.getElementById('srb-map');
		if (!el) { return; }
		const center = this.initialMapCenter();
		const atDefault = center.lat === this.defaultCenter.lat && center.lng === this.defaultCenter.lng;
		this.map = new google.maps.Map(el, {
			center, zoom: atDefault ? this.defaultZoom : 10,
			// Google's own Map/Satellite control is a separate absolutely-positioned
			// overlay that can't coordinate with our floating toolbar and ends up
			// overlapping it. Folded into our own toolbar (mapTypeToggle) instead.
			mapTypeControl: false, streetViewControl: false, gestureHandling: 'greedy',
			clickableIcons: false,
			// Muted, low-saturation basemap so the colored customer/stop pins
			// are the only thing that pops. POI/transit clutter hidden.
			styles: [
				{ elementType: 'geometry', stylers: [{ saturation: -55 }, { lightness: 12 }] },
				{ featureType: 'poi', stylers: [{ visibility: 'off' }] },
				{ featureType: 'transit', stylers: [{ visibility: 'off' }] },
				{ featureType: 'road', elementType: 'geometry', stylers: [{ saturation: -70 }, { lightness: 20 }] },
				{ featureType: 'road', elementType: 'labels', stylers: [{ lightness: 25 }] },
				{ featureType: 'water', stylers: [{ saturation: -35 }, { lightness: 10 }] },
				{ featureType: 'administrative', elementType: 'labels', stylers: [{ lightness: 15 }] },
			],
		});
		// Tap the map to drop a start / end point when in that mode.
		this.map.addListener('click', (e: any) => this.onMapClickSet(e));
		this.refreshMap();
	}

	/** Toggle "tap the map to set the start/end point" mode. */
	setPoint(which: 'start' | 'end'): void {
		this.exitLasso();
		this.settingPoint = this.settingPoint === which ? 'none' : which;
		this.drawHint = this.settingPoint === 'start' ? 'Tap the map to set your start point'
			: this.settingPoint === 'end' ? 'Tap the map to set your end point' : '';
	}
	private onMapClickSet(e: any): void {
		if (!e?.latLng) { return; }
		this.consumeSetPointClick(e.latLng.lat(), e.latLng.lng());
	}
	/**
	 * If "Set start"/"Set end" mode is active, this click sets that point and
	 * returns true — the caller (a marker's own click handler) should bail out
	 * instead of opening its popup. Markers used to swallow the point-setting
	 * tap entirely (Maps JS stops marker clicks from reaching the map's own
	 * click listener), so tapping on or near an existing pin while setting a
	 * point silently did nothing.
	 */
	private consumeSetPointClick(lat: number, lng: number): boolean {
		if (this.settingPoint === 'none') { return false; }
		const which = this.settingPoint;
		if (which === 'start') {
			this.setDayOverride({ home_base_lat: lat, home_base_lng: lng, home_base_label: 'Custom start point' });
		} else {
			this.setDayOverride({ end_mode: 'custom', end_lat: lat, end_lng: lng, end_label: 'Custom end point' });
		}
		this.settingPoint = 'none';
		this.drawHint = '';
		this.hideHover();
		this.closeInfoWin();
		this.status = which === 'start'
			? `Start point set for ${this.activeDayLabel()}.`
			: `End point set for ${this.activeDayLabel()}.`;
		this.afterMutate();
		return true;
	}
	/** Clear a custom end for the active day and return to finishing at the start point. */
	clearEnd(): void { this.setDayOverride({ end_mode: 'home', end_lat: null, end_lng: null, end_label: '' }); this.afterMutate(); }

	/** On the Nearby tab, ring each of the day's stops with the search radius. */
	private paintNearbyRings(): void {
		if (this.sideTab !== 'nearby' || !this.map || typeof google === 'undefined') { return; }
		const meters = this.nearbyRadiusPlan * 1609.34;
		for (const s of this.activeStops) {
			if (!this.hasCoord(s)) { continue; }
			this.nearbyCircles.push(new google.maps.Circle({
				map: this.map, center: { lat: s.lat, lng: s.lng }, radius: meters,
				strokeColor: '#1e88e5', strokeOpacity: 0.35, strokeWeight: 1,
				fillColor: '#1e88e5', fillOpacity: 0.06, clickable: false, zIndex: 1,
			}));
		}
	}

	/** Numbered circle icon for a route stop, colored by execution status. */
	private stopIcon(s: SalesRouteStop): any {
		const fill = s.stop_status === 'done' ? '#43a047'
			: s.stop_status === 'skipped' ? '#90a4ae'
			: s.stop_type === 'prospect' ? '#fb8c00' : '#1e88e5';
		return {
			path: google.maps.SymbolPath.CIRCLE, scale: 12,
			fillColor: fill, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2.5,
		};
	}

	/** Fit the viewport to the day's route (fallback: the loaded account pool). */
	fitRoute(): void {
		if (!this.map || typeof google === 'undefined') { return; }
		const pts = this.activeStops.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
		const src = pts.length ? pts : this.allAccounts.filter((a) => !a.needs_geocode && Number.isFinite(a.lat) && Number.isFinite(a.lng));
		if (!src.length) { return; }
		const bounds = new google.maps.LatLngBounds();
		for (const p of src) { bounds.extend({ lat: p.lat, lng: p.lng }); }
		this.map.fitBounds(bounds, 48);
		if (pts.length === 1) { this.map.setZoom(Math.min(14, this.map.getZoom())); }
	}
	private pointMarkers: any[] = [];
	private nearbyCircles: any[] = [];
	private clearMapOverlays(): void {
		for (const m of this.mapMarkers) { m.setMap(null); }
		this.mapMarkers = [];
		for (const m of this.poolMarkers) { m.setMap(null); }
		this.poolMarkers = [];
		for (const m of this.pointMarkers) { m.setMap(null); }
		this.pointMarkers = [];
		for (const c of this.nearbyCircles) { c.setMap(null); }
		this.nearbyCircles = [];
		this.clearProspectMarkers();
		this.closeInfoWin();
		this.hideHover();
		if (this.routeLine) { this.routeLine.setMap(null); this.routeLine = null; }
	}
	/** Draw the start pin (home base / custom) and the end pin when it's custom. */
	private paintStartEnd(): void {
		if (!this.map || typeof google === 'undefined') { return; }
		const pin = (lat: number, lng: number, fill: string, glyph: string, title: string) => {
			const m = new google.maps.Marker({
				position: { lat, lng }, map: this.map, title, zIndex: 2000,
				label: { text: glyph, color: '#fff', fontSize: '11px', fontWeight: '700' },
				icon: { path: google.maps.SymbolPath.CIRCLE, scale: 11, fillColor: fill, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3 },
			});
			this.pointMarkers.push(m);
		};
		if (this.effHomeBaseLat != null && this.effHomeBaseLng != null) {
			pin(this.effHomeBaseLat, this.effHomeBaseLng, '#2e7d32', 'A', `Start · ${this.effHomeBaseLabel || 'home base'}`);
		}
		if (this.effEndMode === 'custom' && this.effEndLat != null && this.effEndLng != null) {
			pin(this.effEndLat, this.effEndLng, '#c62828', 'B', `End · ${this.effEndLabel || 'custom'}`);
		}
	}

	/** Customer needs a visit — no activity ever, or 30+ days since last contact. */
	private isDueVisit(a: SalesRouteStop): boolean {
		const d = this.lastContactDays(a);
		return d == null || d >= 30;
	}
	/** Teal = a customer in good standing; amber = due a visit. */
	private accountColor(a: SalesRouteStop): string {
		return this.isDueVisit(a) ? '#f4a11e' : '#00897b';
	}

	/**
	 * Paint pool pins as a grid cluster — geocoded accounts not on the active
	 * day are bucketed into screen-pixel cells at the current zoom. Singles are
	 * solid status-colored dots (teal = ok, amber = due a visit); busy cells are
	 * a count bubble (amber-ringed when it contains anyone due) that zooms in on
	 * click. Lasso/select-visible still operate on the full account list.
	 */
	private repaintPool(): void {
		if (!this.map || typeof google === 'undefined') { return; }
		for (const m of this.poolMarkers) { m.setMap(null); }
		this.poolMarkers = [];
		const projection = this.map.getProjection();
		if (!projection) { return; }
		const scale = Math.pow(2, this.map.getZoom());
		const cell = this.clusterCellPx;
		const onDay = new Set(this.activeStops.map((s) => this.stopKey(s)));

		const cells = new Map<string, SalesRouteStop[]>();
		// On the Nearby tab the map shows exactly the nearby list (from the full
		// account set, not the customers-tab filter, so map and list agree).
		// Otherwise mirror the customers-list filter/search so filtering narrows
		// the pins (and speeds up rendering), not just the side list.
		const nearbyKeys = this.sideTab === 'nearby' ? this.nearbyKeySet() : null;
		const source = nearbyKeys ? this.allAccounts : this.filteredAccounts();
		for (const a of source) {
			if (a.needs_geocode || !Number.isFinite(a.lat) || !Number.isFinite(a.lng)) { continue; }
			if (onDay.has(this.stopKey(a))) { continue; }
			if (nearbyKeys && !nearbyKeys.has(this.stopKey(a))) { continue; }
			const wp = projection.fromLatLngToPoint(new google.maps.LatLng(a.lat, a.lng));
			const key = `${Math.floor((wp.x * scale) / cell)}:${Math.floor((wp.y * scale) / cell)}`;
			const bucket = cells.get(key);
			if (bucket) { bucket.push(a); } else { cells.set(key, [a]); }
		}

		for (const bucket of cells.values()) {
			if (bucket.length === 1) {
				const a = bucket[0];
				const marker = new google.maps.Marker({
					position: { lat: a.lat, lng: a.lng }, map: this.map,
					icon: { path: google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: this.accountColor(a), fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
				});
				this.attachHover(marker, this.stopKey(a), { lat: a.lat, lng: a.lng }, this.accountTip(a));
				marker.addListener('click', () => { if (this.consumeSetPointClick(a.lat, a.lng)) { return; } this.openCustomer360(a); });
				this.poolMarkers.push(marker);
			} else {
				let slat = 0, slng = 0, due = 0;
				for (const a of bucket) { slat += a.lat; slng += a.lng; if (this.isDueVisit(a)) { due++; } }
				const center = { lat: slat / bucket.length, lng: slng / bucket.length };
				// Grow gently with count; amber ring flags "customers here need a visit".
				const cScale = Math.min(19, 12 + Math.log2(bucket.length) * 2.2);
				const marker = new google.maps.Marker({
					position: center, map: this.map,
					label: { text: String(bucket.length), color: '#fff', fontSize: '12px', fontWeight: '700' },
					icon: {
						path: google.maps.SymbolPath.CIRCLE, scale: cScale,
						fillColor: '#00695c', fillOpacity: 0.95,
						strokeColor: due ? '#f4a11e' : '#fff', strokeWeight: due ? 3.5 : 2,
					},
				});
				// Do the members sit almost on top of each other (same salon /
				// same plaza)? Then zooming never splits them — list them instead so
				// each branch can actually be opened and added.
				let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
				for (const a of bucket) { minLat = Math.min(minLat, a.lat); maxLat = Math.max(maxLat, a.lat); minLng = Math.min(minLng, a.lng); maxLng = Math.max(maxLng, a.lng); }
				const coincident = (maxLat - minLat) < 0.0006 && (maxLng - minLng) < 0.0006;
				const ctip = `<strong>${bucket.length} customers here</strong>`
					+ (due ? `<br><span class="due">${due} due a visit</span>` : '')
					+ `<br><span>${coincident ? 'tap to pick one' : 'tap to zoom in'}</span>`;
				this.attachHover(marker, `c|${center.lat.toFixed(4)},${center.lng.toFixed(4)}`, center, ctip);
				marker.addListener('click', () => {
					if (this.consumeSetPointClick(center.lat, center.lng)) { return; }
					this.hideHover();
					if (coincident || this.map.getZoom() >= 17) { this.openClusterPicker(bucket, center); }
					else { this.map.panTo(center); this.map.setZoom(Math.min(18, this.map.getZoom() + 2)); }
				});
				this.poolMarkers.push(marker);
			}
		}
	}
	private refreshMap(): void {
		if (!this.map || typeof google === 'undefined') { return; }
		this.clearMapOverlays();

		// Re-cluster the pool pins as the user zooms/pans (attached once).
		if (!this.clusterListenerAttached) {
			this.clusterListenerAttached = true;
			this.map.addListener('idle', () => this.repaintPool());
		}
		this.repaintPool();
		this.repaintProspects();
		this.paintStartEnd();
		this.paintNearbyRings();

		const path: { lat: number; lng: number }[] = [];
		this.activeStops.forEach((s, i) => {
			if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) { return; }
			path.push({ lat: s.lat, lng: s.lng });
			const marker = new google.maps.Marker({
				position: { lat: s.lat, lng: s.lng }, map: this.map,
				label: { text: String(i + 1), color: '#fff', fontSize: '12px', fontWeight: '700' },
				icon: this.stopIcon(s), zIndex: 1000 + i,
			});
			const stat = s.stop_status === 'done' ? ' · visited' : s.stop_status === 'skipped' ? ' · skipped' : '';
			const stip = `<strong>#${i + 1} ${this.escHtml(this.accountLabel(s))}</strong>${stat}`
				+ (this.stopAddress(s) ? `<br><span>${this.escHtml(this.stopAddress(s))}</span>` : '');
			this.attachHover(marker, `s|${i}|${this.stopKey(s)}`, { lat: s.lat, lng: s.lng }, stip);
			marker.addListener('click', () => { if (this.consumeSetPointClick(s.lat, s.lng)) { return; } this.hideHover(); this.openStopInfo(s, i); });
			this.mapMarkers.push(marker);
		});
		if (path.length > 1) {
			this.routeLine = new google.maps.Polyline({
				path, geodesic: true, strokeColor: '#1e88e5', strokeOpacity: 0.85, strokeWeight: 3.5, map: this.map,
				// Direction arrows so the drive order reads at a glance.
				icons: [{
					icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.4, strokeColor: '#1565c0', strokeWeight: 1.5 },
					offset: '30px', repeat: '90px',
				}],
			});
		}

		// Fit ONLY when the day changes or a route first appears — refitting on
		// every add/remove yanks the viewport out from under the user.
		if (path.length && (this.lastFitDow !== this.activeDow || this.lastPathLen === 0)) {
			this.fitRoute();
			this.lastFitDow = this.activeDow;
		} else if (!path.length && !this.didPoolFit && this.allAccounts.some((a) => !a.needs_geocode)) {
			// First load with no route yet: frame the account pool once.
			this.didPoolFit = true;
			this.fitRoute();
		}
		this.lastPathLen = path.length;
	}
	// Fallback view before any accounts load: Western/Central NY, where the
	// territory lives — never the whole globe.
	private readonly defaultCenter = { lat: 42.95, lng: -77.5 };
	private readonly defaultZoom = 7;
	private initialMapCenter(): { lat: number; lng: number } {
		if (this.homeBaseLat != null && this.homeBaseLng != null) { return { lat: this.homeBaseLat, lng: this.homeBaseLng }; }
		const pool = this.activeStops.length ? this.activeStops : this.allAccounts;
		for (const s of pool) { if (Number.isFinite(s.lat) && Number.isFinite(s.lng)) { return { lat: s.lat, lng: s.lng }; } }
		return this.defaultCenter;
	}

	/** "Name · Branch · City" — branch is dropped when it just repeats the
	 *  customer name (common for single-branch accounts), avoiding "X · X · City". */
	accountLabel(a: SalesRouteStop): string {
		const name = String(a.name || '').trim();
		const br = String(a.brname || '').trim();
		const city = String(a.city || '').trim();
		const parts = [name, br && br.toUpperCase() !== name.toUpperCase() ? br : '', city];
		return parts.filter((x) => x).join(' · ');
	}
	stopAddress(s: SalesRouteStop): string { return [s.address, s.city].filter((x) => String(x || '').trim()).join(', '); }

	// ── map type (folded into our own toolbar — see tryInitMap) ──────
	mapTypeId: 'roadmap' | 'satellite' = 'roadmap';
	toggleMapType(): void {
		this.mapTypeId = this.mapTypeId === 'roadmap' ? 'satellite' : 'roadmap';
		if (this.map) { this.map.setMapTypeId(this.mapTypeId); }
	}
	// Toolbar starts compact (Map/Satellite + Fit + Leads only) so it never
	// spans wide enough to crowd the map; the rest expands on demand.
	toolsExpanded = false;
	toggleTools(): void { this.toolsExpanded = !this.toolsExpanded; }

	// ── lasso / bulk select on the map ──────────────────────────────
	setDrawMode(mode: 'none' | 'lasso' | 'lasso_remove'): void {
		if (mode === 'lasso' || mode === 'lasso_remove') { this.enterLasso(mode === 'lasso_remove'); return; }
		this.exitLasso();
	}

	/** Add every geocoded pool account currently inside the map viewport. */
	selectVisible(): void {
		if (!this.map) { return; }
		const bounds = this.map.getBounds();
		if (!bounds) { return; }
		this.snapshotForUndo('add shown');
		const placed = new Set(this.allPlacedStops().map((s) => this.stopKey(s)));
		let added = 0, leads = 0;
		for (const a of this.allAccounts) {
			if (a.needs_geocode || !Number.isFinite(a.lat) || !Number.isFinite(a.lng)) { continue; }
			if (placed.has(this.stopKey(a))) { continue; }
			if (bounds.contains(new google.maps.LatLng(a.lat, a.lng))) { this.routesByDay[this.activeDow].push({ ...a }); placed.add(this.stopKey(a)); added++; }
		}
		// Leads that are on the map (layer shown) get swept in too.
		if (this.showProspects) {
			for (const p of this.matchingProspects()) {
				if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) { continue; }
				const stop = this.stopFromProspect(p);
				if (placed.has(this.stopKey(stop))) { continue; }
				if (bounds.contains(new google.maps.LatLng(p.lat, p.lng))) { this.routesByDay[this.activeDow].push(stop); placed.add(this.stopKey(stop)); leads++; }
			}
		}
		if (added || leads) { this.status = `Added ${this.addedSummary(added, leads)} to ${this.days[this.activeDow].label}.`; this.afterMutate(); }
	}
	/** "3 customers + 2 leads" style summary for bulk-add feedback. */
	private addedSummary(customers: number, leads: number): string {
		const parts: string[] = [];
		if (customers) { parts.push(`${customers} customer${customers === 1 ? '' : 's'}`); }
		if (leads) { parts.push(`${leads} lead${leads === 1 ? '' : 's'}`); }
		return parts.join(' + ') || 'nothing';
	}

	private enterLasso(removeMode: boolean): void {
		this.exitLasso();
		this.settingPoint = 'none';
		this.drawMode = removeMode ? 'lasso_remove' : 'lasso';
		const canvas = this.lassoCanvas?.nativeElement;
		if (!canvas) { return; }
		canvas.removeAttribute('hidden');
		if (this.map) {
			this.lassoSavedGest = { gestureHandling: this.map.get('gestureHandling'), draggable: this.map.get('draggable'), scrollwheel: this.map.get('scrollwheel') };
			this.map.setOptions({ gestureHandling: 'none', draggable: false, scrollwheel: false });
		}
		canvas.addEventListener('pointerdown', this.lassoDown);
		canvas.addEventListener('pointermove', this.lassoMove);
		canvas.addEventListener('pointerup', this.lassoUp);
		canvas.addEventListener('pointercancel', this.lassoUp);
		canvas.addEventListener('touchstart', this.blockTouch, { passive: false } as any);
		canvas.addEventListener('touchmove', this.blockTouch, { passive: false } as any);
		this.drawHint = removeMode ? 'Draw around stops to remove' : 'Draw around accounts to add';
	}

	private exitLasso(): void {
		this.drawMode = 'none';
		this.drawHint = '';
		this.lassoDrawing = false;
		this.lassoPts = [];
		this.lassoPointerId = null;
		const canvas = this.lassoCanvas?.nativeElement;
		if (canvas) {
			canvas.setAttribute('hidden', '');
			canvas.removeEventListener('pointerdown', this.lassoDown);
			canvas.removeEventListener('pointermove', this.lassoMove);
			canvas.removeEventListener('pointerup', this.lassoUp);
			canvas.removeEventListener('pointercancel', this.lassoUp);
			canvas.removeEventListener('touchstart', this.blockTouch);
			canvas.removeEventListener('touchmove', this.blockTouch);
			const path = canvas.querySelector('.srb-lasso-path') as SVGPathElement | null;
			if (path) { path.setAttribute('d', ''); }
		}
		if (this.map && this.lassoSavedGest) { this.map.setOptions(this.lassoSavedGest); this.lassoSavedGest = null; }
	}

	private onLassoDown(e: PointerEvent): void {
		if (this.lassoPointerId !== null) { return; }
		const canvas = this.lassoCanvas.nativeElement;
		try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
		this.lassoPointerId = e.pointerId;
		this.lassoDrawing = true;
		const rect = canvas.getBoundingClientRect();
		this.lassoPts = [{ x: e.clientX - rect.left, y: e.clientY - rect.top }];
		this.renderLasso();
		e.preventDefault();
	}
	private onLassoMove(e: PointerEvent): void {
		if (!this.lassoDrawing || e.pointerId !== this.lassoPointerId) { return; }
		const canvas = this.lassoCanvas.nativeElement;
		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left, y = e.clientY - rect.top;
		const last = this.lassoPts[this.lassoPts.length - 1];
		if (last && Math.abs(x - last.x) < 2 && Math.abs(y - last.y) < 2) { return; }
		this.lassoPts.push({ x, y });
		this.renderLasso();
		e.preventDefault();
	}
	private onLassoUp(e: PointerEvent): void {
		if (e.pointerId !== this.lassoPointerId) { return; }
		this.lassoDrawing = false;
		this.lassoPointerId = null;
		try { this.lassoCanvas.nativeElement.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
		const pts = this.lassoPts.slice();
		const remove = this.drawMode === 'lasso_remove';
		if (pts.length < 3) { this.exitLasso(); return; }
		this.exitLasso();
		remove ? this.lassoRemove(pts) : this.lassoAdd(pts);
	}
	private renderLasso(): void {
		const path = this.lassoCanvas?.nativeElement.querySelector('.srb-lasso-path') as SVGPathElement | null;
		if (!path) { return; }
		if (!this.lassoPts.length) { path.setAttribute('d', ''); return; }
		let d = `M${this.lassoPts[0].x} ${this.lassoPts[0].y}`;
		for (let i = 1; i < this.lassoPts.length; i++) { d += ` L${this.lassoPts[i].x} ${this.lassoPts[i].y}`; }
		path.setAttribute('d', this.lassoDrawing ? d : `${d} Z`);
	}
	private lassoAdd(pts: Array<{ x: number; y: number }>): void {
		this.snapshotForUndo('circle add');
		const placed = new Set(this.allPlacedStops().map((s) => this.stopKey(s)));
		let added = 0, leads = 0;
		for (const a of this.allAccounts) {
			if (a.needs_geocode || !Number.isFinite(a.lat) || !Number.isFinite(a.lng)) { continue; }
			if (placed.has(this.stopKey(a))) { continue; }
			const px = this.latLngToPixel(a.lat, a.lng);
			if (px && this.pointInPoly(px.x, px.y, pts)) { this.routesByDay[this.activeDow].push({ ...a }); placed.add(this.stopKey(a)); added++; }
		}
		// Leads shown on the map are lasso-able too.
		if (this.showProspects) {
			for (const p of this.matchingProspects()) {
				if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) { continue; }
				const stop = this.stopFromProspect(p);
				if (placed.has(this.stopKey(stop))) { continue; }
				const px = this.latLngToPixel(p.lat, p.lng);
				if (px && this.pointInPoly(px.x, px.y, pts)) { this.routesByDay[this.activeDow].push(stop); placed.add(this.stopKey(stop)); leads++; }
			}
		}
		this.status = (added || leads) ? `Added ${this.addedSummary(added, leads)} to ${this.days[this.activeDow].label}.` : 'Nothing inside the lasso.';
		this.afterMutate();
	}
	private lassoRemove(pts: Array<{ x: number; y: number }>): void {
		this.snapshotForUndo('circle remove');
		const keep: SalesRouteStop[] = [];
		let removed = 0;
		for (const s of this.activeStops) {
			const px = (Number.isFinite(s.lat) && Number.isFinite(s.lng)) ? this.latLngToPixel(s.lat, s.lng) : null;
			if (px && this.pointInPoly(px.x, px.y, pts)) { removed++; } else { keep.push(s); }
		}
		this.routesByDay[this.activeDow] = keep;
		this.status = removed ? `Removed ${removed} stop(s).` : 'Nothing inside the lasso.';
		this.afterMutate();
	}
	private latLngToPixel(lat: number, lng: number): { x: number; y: number } | null {
		const projection = this.map?.getProjection();
		if (!projection) { return null; }
		const bounds = this.map.getBounds();
		if (!bounds) { return null; }
		const nw = projection.fromLatLngToPoint(bounds.getNorthEast());
		const se = projection.fromLatLngToPoint(bounds.getSouthWest());
		const wp = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
		const host = this.mapHost?.nativeElement;
		const w = host ? host.offsetWidth : 0, h = host ? host.offsetHeight : 0;
		return { x: ((wp.x - se.x) / (nw.x - se.x)) * w, y: ((wp.y - nw.y) / (se.y - nw.y)) * h };
	}
	/** Even-odd ray casting (works on self-intersecting paths). */
	private pointInPoly(x: number, y: number, pts: Array<{ x: number; y: number }>): boolean {
		let inside = false;
		for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
			const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
			if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-12) + xi)) { inside = !inside; }
		}
		return inside;
	}
}
